import React, { useState, useMemo, useEffect, useRef } from 'react';
import { BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { GradeEntry, Student, AgendaEntry, LearningObjective, AssessmentGrade, SasGrade, ActivityType, FormativeGradeTP, FormativeLevel, Teacher, Tugas, TugasSubmission, AttendanceRecord, AttendanceStatus, UserRole } from '../types.ts';
import { Save, Search, Filter, ArrowUpRight, BookMarked, Target, FileText, CheckCircle2, ChevronRight, ChevronLeft, PenTool, ClipboardCheck, GraduationCap, X, LayoutGrid, ClipboardList, BarChartHorizontal, FileSpreadsheet, Trash2, BarChart3, PieChart as PieChartIcon, TrendingUp, UserX, UserPlus, Award, BookOpen, Clock, User, ArrowUpDown, Printer, Upload, Download, Plus, PlusCircle, Send, Sparkles, Check, AlertCircle, Share2, ArrowRight, Loader2 } from 'lucide-react';
import ExcelJS from 'exceljs';
import { db } from '../src/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../src/lib/firestore-errors';
import { getThemeById } from '../src/themeConfig.ts';

interface GradingProps {
  students: Student[];
  agendas: AgendaEntry[];
  objectives: LearningObjective[];
  assessmentGrades: AssessmentGrade[];
  sasGrades: SasGrade[];
  formativeGradesTP: FormativeGradeTP[];
  tugas?: Tugas[];
  tugasSubmissions?: TugasSubmission[];
  onUpdateGrades: (grades: AssessmentGrade[]) => void;
  onUpdateSasGrades: (grades: SasGrade[]) => void;
  onUpdateFormativeTP: (grades: FormativeGradeTP[]) => void;
  onUpdateObjectives?: (objectives: LearningObjective[]) => void;
  onUpdateTeacher?: (teacher: Teacher) => void;
  activeUser: Teacher;
  schoolSettings: any;
  initialAgenda?: AgendaEntry | null;
  attendanceRecords?: AttendanceRecord[];
  selectedStudentId?: string;
  onStudentRecapClick?: (student: Student) => void;
  role?: UserRole;
  appTheme?: string;
}

const GradingView: React.FC<GradingProps> = ({ 
  students, 
  agendas, 
  objectives, 
  assessmentGrades, 
  sasGrades,
  formativeGradesTP,
  tugas = [],
  tugasSubmissions = [],
  onUpdateGrades, 
  onUpdateSasGrades,
  onUpdateFormativeTP,
  onUpdateObjectives,
  onUpdateTeacher,
  activeUser,
  schoolSettings,
  initialAgenda,
  attendanceRecords = [],
  selectedStudentId,
  onStudentRecapClick,
  role,
  appTheme = 'wzc-themes'
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [activeTab, setActiveTab] = useState<'sumatif' | 'formatif' | 'sumatif-akhir' | 'rekap' | 'rekap-buku' | 'kuis'>('rekap');
  
  useEffect(() => {
    if (selectedStudentId && students.length > 0) {
      const student = students.find(s => s.id === selectedStudentId);
      if (student) {
        setRekapClass(student.class);
        setActiveTab('rekap');
        
        // Scroll to the student row
        setTimeout(() => {
            const row = document.getElementById(`grad-student-${student.id}`);
            if (row) {
                row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                row.classList.add('bg-indigo-50', 'dark:bg-indigo-900/40');
                setTimeout(() => {
                    row.classList.remove('bg-indigo-50', 'dark:bg-indigo-900/40');
                }, 3000);
            }
        }, 300);
      }
    }
  }, [selectedStudentId]);
  
  // Helper for class navigation
  const sortedClasses = useMemo(() => [...activeUser.assignedClasses].sort(), [activeUser.assignedClasses]);

  const navigateClass = (current: string, direction: 'prev' | 'next', setter: (c: string) => void) => {
    if (sortedClasses.length === 0) return;
    const currentIndex = sortedClasses.indexOf(current);
    if (currentIndex === -1) {
      setter(sortedClasses[0]);
      return;
    }
    let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= sortedClasses.length) newIndex = 0;
    if (newIndex < 0) newIndex = sortedClasses.length - 1;
    setter(sortedClasses[newIndex]);
  };
  
  // States for Sumatif
  const [selectedAssessment, setSelectedAssessment] = useState<AgendaEntry | null>(null);
  const [scores, setScores] = useState<Record<string, number | string>>({});
  const [assessmentType, setAssessmentType] = useState<'Sumatif per TP' | 'KBM dan quiz (jika ada)' | 'Sumatif Akhir'>('Sumatif per TP');

  // States for Sumatif Akhir
  const [sasClass, setSasClass] = useState<string>(activeUser.assignedClasses[0] || '');
  const [sasScores, setSasScores] = useState<Record<string, number>>({});
  const [sasSortConfig, setSasSortConfig] = useState<{ key: 'name' | 'score' | 'percentage' | 'alpa' | 'nilaiTh' | 'nilaiCatatan' | 'nilaiEvaluasi', direction: 'asc' | 'desc' }>({ key: 'name', direction: 'asc' });
  const [isSavingSas, setIsSavingSas] = useState(false);
  const [classFilter, setClassFilter] = useState<string>('SEMUA');
  const [typeFilter, setTypeFilter] = useState<ActivityType | 'SEMUA'>('SEMUA');
  const [statusFilter, setStatusFilter] = useState<'SEMUA' | 'SUDAH' | 'BELUM'>('SEMUA');
  const [tpFilter, setTpFilter] = useState<string>('SEMUA');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');

  const [weightDaily, setWeightDaily] = useState<number>(() => {
    return activeUser?.weightDaily ?? (localStorage.getItem('grading_weight_daily') ? Number(localStorage.getItem('grading_weight_daily')) : 20);
  });
  const [weightSas, setWeightSas] = useState<number>(() => {
    return activeUser?.weightSas ?? (localStorage.getItem('grading_weight_sas') ? Number(localStorage.getItem('grading_weight_sas')) : 80);
  });

  useEffect(() => {
    if (activeUser?.weightDaily !== undefined) {
      setWeightDaily(activeUser.weightDaily);
    }
    if (activeUser?.weightSas !== undefined) {
      setWeightSas(activeUser.weightSas);
    }
  }, [activeUser?.weightDaily, activeUser?.weightSas]);

  const handleWeightDailyChange = (val: number) => {
    const safeVal = Math.max(0, Math.min(100, val));
    setWeightDaily(safeVal);
    setWeightSas(100 - safeVal);
    localStorage.setItem('grading_weight_daily', String(safeVal));
    localStorage.setItem('grading_weight_sas', String(100 - safeVal));
    if (onUpdateTeacher && activeUser) {
      onUpdateTeacher({
        ...activeUser,
        weightDaily: safeVal,
        weightSas: 100 - safeVal
      });
    }
  };

  const handleWeightSasChange = (val: number) => {
    const safeVal = Math.max(0, Math.min(100, val));
    setWeightSas(safeVal);
    setWeightDaily(100 - safeVal);
    localStorage.setItem('grading_weight_sas', String(safeVal));
    localStorage.setItem('grading_weight_daily', String(100 - safeVal));
    if (onUpdateTeacher && activeUser) {
      onUpdateTeacher({
        ...activeUser,
        weightSas: safeVal,
        weightDaily: 100 - safeVal
      });
    }
  };


  // States for Formatif TP
  const [selectedClass, setSelectedClass] = useState<string>(activeUser.assignedClasses[0] || '');
  const [selectedTP, setSelectedTP] = useState<string>(objectives.length > 0 ? objectives[0].id : '');
  const [formativeValues, setFormativeValues] = useState<Record<string, FormativeLevel>>({});
  const [praktikumValues, setPraktikumValues] = useState<Record<string, number>>({});
  const [summativeValues, setSummativeValues] = useState<Record<string, number>>({});
  const selectedTPData = useMemo(() => {
    const tp = objectives.find(o => o.id === selectedTP);
    if (tp && tp.isPracticum) {
        return { ...tp, isPracticum: true };
    }
    return tp;
  }, [objectives, selectedTP]);
  
  // Update selectedTP when objectives are loaded
  useEffect(() => {
    if (!selectedTP && objectives.length > 0) {
      setSelectedTP(objectives[0].id);
    }
  }, [objectives, selectedTP]);

  // Sync selectedClass, sasClass, and rekapClass with activeUser.assignedClasses
  useEffect(() => {
    if (activeUser?.assignedClasses && activeUser.assignedClasses.length > 0) {
      const sortedClasses = [...activeUser.assignedClasses].sort();
      const firstClass = sortedClasses[0];
      if (!sasClass || !sortedClasses.includes(sasClass)) {
        setSasClass(firstClass);
      }
      if (!selectedClass || !sortedClasses.includes(selectedClass)) {
        setSelectedClass(firstClass);
      }
      if (!rekapClass || !sortedClasses.includes(rekapClass)) {
        setRekapClass(firstClass);
      }
    }
  }, [activeUser?.assignedClasses]);
  
  // States for Rekapitulasi
  const [rekapClass, setRekapClass] = useState<string>(activeUser.assignedClasses[0] || '');
  const [rekapAssessmentType, setRekapAssessmentType] = useState<string>('Semua');
  const [rekapSortConfig, setRekapSortConfig] = useState<{ key: 'studentName' | 'average' | 'total', direction: 'asc' | 'desc' }>({ key: 'studentName', direction: 'asc' });
  const [sumatifWeight, setSumatifWeight] = useState<number>(activeUser.sumatifWeight || 60);
  const [praktikumWeight, setPraktikumWeight] = useState<number>(activeUser.praktikumWeight || 40);
  const [isSavingWeights, setIsSavingWeights] = useState(false);
  const [recapValues, setRecapValues] = useState<Record<string, Record<string, number>>>({}); // assessmentId -> studentId -> score
  const [assessmentToDelete, setAssessmentToDelete] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [selectedStudentForSummary, setSelectedStudentForSummary] = useState<Student | null>(null);



  // States for Penilaian Rekap Buku Catatan
  const [notebookClass, setNotebookClass] = useState<string>(activeUser.assignedClasses[0] || '');
  const [notebookSearch, setNotebookSearch] = useState<string>('');
  const [notebookEntries, setNotebookEntries] = useState<Record<string, { jumlahTtd: number; notes: string }>>({});
  const [isSavingNotebook, setIsSavingNotebook] = useState(false);

  // Sync notebook class
  useEffect(() => {
    if (activeUser?.assignedClasses && activeUser.assignedClasses.length > 0) {
      const sortedClasses = [...activeUser.assignedClasses].sort();
      if (!notebookClass || !sortedClasses.includes(notebookClass)) {
        setNotebookClass(sortedClasses[0]);
      }
    }
  }, [activeUser?.assignedClasses, notebookClass]);

  // Load notebook entries from Firebase Firestore & localStorage fallback
  useEffect(() => {
    if (!notebookClass || !activeUser?.id) return;
    let isMounted = true;
    const loadNotebook = async () => {
      const storageKey = `smpn1_notebook_rekap_${activeUser.id}_${notebookClass}`;
      // Load local cache first for instant response
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved && isMounted) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === 'object') {
            if (parsed.entries) setNotebookEntries(parsed.entries);
            else setNotebookEntries(parsed);
          }
        }
      } catch (e) {}

      // Fetch from Firebase Firestore
      if (db) {
        try {
          const docId = `${activeUser.id}_${notebookClass}`;
          const docRef = doc(db, 'notebookRecords', docId);
          const snap = await getDoc(docRef);
          if (snap.exists() && isMounted) {
            const data = snap.data();
            if (data && data.entries) {
              setNotebookEntries(data.entries);
              localStorage.setItem(storageKey, JSON.stringify(data));
            }
          }
        } catch (err) {
          console.error('Error loading notebook records from Firebase:', err);
        }
      }
    };
    loadNotebook();
    return () => { isMounted = false; };
  }, [notebookClass, activeUser?.id]);

  const handleSaveNotebook = async (isAuto = false, customEntries?: Record<string, { jumlahTtd: number; notes: string }>) => {
    if (!notebookClass || !activeUser?.id) return;
    setIsSavingNotebook(true);
    try {
      const storageKey = `smpn1_notebook_rekap_${activeUser.id}_${notebookClass}`;
      const entriesToSave = customEntries || notebookEntries;
      const payload = {
        id: `${activeUser.id}_${notebookClass}`,
        teacherId: activeUser.id,
        class: notebookClass,
        entries: entriesToSave,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));

      if (db) {
        const docRef = doc(db, 'notebookRecords', payload.id);
        await setDoc(docRef, payload, { merge: true });
      }

      if (!isAuto) {
        setSaveSuccessMessage('Penilaian Rekap Buku Catatan berhasil disimpan ke Firebase!');
        setShowSaveSuccessModal(true);
        setTimeout(() => setShowSaveSuccessModal(false), 2000);
      }
    } catch (err: any) {
      console.error('Failed to save notebook records:', err);
      if (err?.code === 'permission-denied') {
        handleFirestoreError(err, OperationType.WRITE, 'notebookRecords');
      }
    } finally {
      setTimeout(() => setIsSavingNotebook(false), 300);
    }
  };

  const handleTtdChange = (studentId: string, delta: number) => {
    const current = notebookEntries[studentId] || { jumlahTtd: 0, notes: '' };
    const newTtd = Math.max(0, Math.min(99, (current.jumlahTtd || 0) + delta));
    const updated = {
      ...notebookEntries,
      [studentId]: {
        ...current,
        jumlahTtd: newTtd
      }
    };
    setNotebookEntries(updated);
    handleSaveNotebook(true, updated);
  };

  const handleTtdSetDirect = (studentId: string, val: string) => {
    const num = parseInt(val, 10);
    const clamped = isNaN(num) ? 0 : Math.max(0, Math.min(99, num));
    const current = notebookEntries[studentId] || { jumlahTtd: 0, notes: '' };
    const updated = {
      ...notebookEntries,
      [studentId]: {
        ...current,
        jumlahTtd: clamped
      }
    };
    setNotebookEntries(updated);
    handleSaveNotebook(true, updated);
  };

  const handleNotebookNotesChange = (studentId: string, text: string, shouldSave = false) => {
    const current = notebookEntries[studentId] || { jumlahTtd: 0, notes: '' };
    const updated = {
      ...notebookEntries,
      [studentId]: {
        ...current,
        notes: text
      }
    };
    setNotebookEntries(updated);
    if (shouldSave) {
      handleSaveNotebook(true, updated);
    }
  };

  const handleBatchSetTtd = (count: number) => {
    const classStudents = students.filter(s => s.class === notebookClass && s.status === 'Aktif');
    const updated = { ...notebookEntries };
    classStudents.forEach(s => {
      const current = updated[s.id] || { jumlahTtd: 0, notes: '' };
      updated[s.id] = { ...current, jumlahTtd: count };
    });
    setNotebookEntries(updated);
    handleSaveNotebook(true, updated);
  };

  const filteredNotebookStudents = useMemo(() => {
    if (!notebookClass) return [];
    let list = students.filter(s => s.class === notebookClass && s.status === 'Aktif');
    if (notebookSearch.trim()) {
      const q = notebookSearch.toLowerCase().trim();
      list = list.filter(s => s.name.toLowerCase().includes(q) || (s.nisn && s.nisn.includes(q)) || (s.nis && s.nis.includes(q)));
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [students, notebookClass, notebookSearch]);

  const notebookStats = useMemo(() => {
    const classStudents = students.filter(s => s.class === notebookClass && s.status === 'Aktif');
    const total = classStudents.length;
    let totalTtd = 0;
    let maxTtd = 0;
    let maxTtdStudents: string[] = [];

    classStudents.forEach(s => {
      const ttd = notebookEntries[s.id]?.jumlahTtd || 0;
      totalTtd += ttd;
      if (ttd > maxTtd) {
        maxTtd = ttd;
        maxTtdStudents = [s.name];
      } else if (ttd === maxTtd && ttd > 0) {
        maxTtdStudents.push(s.name);
      }
    });

    const avgTtd = total > 0 ? (totalTtd / total).toFixed(1) : '0';
    return { total, totalTtd, avgTtd, maxTtd, maxTtdStudents };
  }, [students, notebookClass, notebookEntries]);

  // Helper for class name normalization
  const normalizeClass = (cls?: string) => {
    if (!cls) return '';
    let str = cls.toString().trim().toUpperCase().replace(/\s+/g, '');
    str = str.replace(/^IX/, '9').replace(/^VIII/, '8').replace(/^VII/, '7');
    return str;
  };

  // States for Penilaian Kuis
  const [quizClass, setQuizClass] = useState<string>('');
  const [selectedQuizAgendaId, setSelectedQuizAgendaId] = useState<string>('');
  const [quizScores, setQuizScores] = useState<Record<string, number | string>>({});
  const [quizSearch, setQuizSearch] = useState<string>('');
  const [quizFilterMode, setQuizFilterMode] = useState<'all' | 'ungraded'>('all');
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteInputText, setPasteInputText] = useState('');

  // States for sending daily assignment grades to Sumatif TP
  const [showSendToSumatifModal, setShowSendToSumatifModal] = useState(false);
  const [targetSumatifTpId, setTargetSumatifTpId] = useState<string>('');
  const [targetSumatifActivityType, setTargetSumatifActivityType] = useState<ActivityType.SUMATIF | ActivityType.PRAKTIKUM>(ActivityType.SUMATIF);
  const [isSendingToSumatif, setIsSendingToSumatif] = useState(false);

  // All teacher's quiz agendas across all classes (accurately matched to active teacher)
  const allTeacherQuizAgendas = useMemo(() => {
    const teacherId = activeUser.id;
    const teacherUsername = activeUser.username;
    const teacherNip = activeUser.nip;
    const teacherNameNorm = (activeUser.name || '').trim().toLowerCase();
    const teacherSubjectNorm = (activeUser.subject || '').trim().toLowerCase();
    const assignedClassesNorm = (activeUser.assignedClasses || []).map(c => normalizeClass(c));

    return (agendas || []).filter(a => {
      // 1. Strict teacher matching
      let isTeacherMatch = false;

      if (a.teacherId) {
        // If teacherId is explicitly set, it MUST match activeUser's credentials
        isTeacherMatch = (a.teacherId === teacherId || a.teacherId === teacherUsername || (!!teacherNip && a.teacherId === teacherNip));
      } else if (a.teacherName) {
        isTeacherMatch = a.teacherName.trim().toLowerCase() === teacherNameNorm;
      } else if (a.subject && teacherSubjectNorm) {
        const classNorm = normalizeClass(a.class);
        isTeacherMatch = a.subject.trim().toLowerCase() === teacherSubjectNorm &&
                         (assignedClassesNorm.length === 0 || assignedClassesNorm.includes(classNorm));
      } else {
        const classNorm = normalizeClass(a.class);
        isTeacherMatch = assignedClassesNorm.length === 0 || assignedClassesNorm.includes(classNorm);
      }

      if (!isTeacherMatch) return false;

      // 2. Pembelajaran dan Asesmen criteria (specifically for Pembelajaran dan Asesmen / KBM dan Asesmen Harian)
      const isPemahamanType = a.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN ||
                              (a.activityType as any) === 'Pembelajaran dan Asesmen' ||
                              (a.activityType as any) === 'KBM dan Asesmen Harian';

      return isPemahamanType;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [agendas, activeUser]);

  // List of agendas that are not fully graded yet by the teacher across all classes
  const ungradedQuizAgendas = useMemo(() => {
    return allTeacherQuizAgendas.map(agenda => {
      const classNorm = normalizeClass(agenda.class);
      const classStudents = students.filter(s => normalizeClass(s.class) === classNorm && s.status === 'Aktif');
      const existingGrade = assessmentGrades.find(g => g.id === agenda.id);
      const scoresObj = existingGrade?.scores || recapValues[agenda.id] || {};
      
      let gradedCount = 0;
      let presentStudentCount = 0;
      let absentStudentCount = 0;

      classStudents.forEach(s => {
        const attRecord = attendanceRecords.find(r => r.studentId === s.id && r.agendaId === agenda.id);
        const attStatusLower = attRecord?.status?.trim().toLowerCase();
        const isAbsent = attStatusLower && (
          attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
          attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
          attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
          attStatusLower === AttendanceStatus.Izin.toLowerCase()
        );

        if (isAbsent) {
          absentStudentCount++;
        } else {
          presentStudentCount++;
          const val = scoresObj[s.id];
          if (val !== undefined && val !== null && String(val).trim() !== '') {
            gradedCount++;
          }
        }
      });

      const totalStudents = classStudents.length;
      // Siswa tidak hadir tidak dapat nilai, jadi hanya siswa hadir yang butuh penilaian
      const isUngraded = presentStudentCount > 0 ? gradedCount < presentStudentCount : false;
      const ungradedCount = Math.max(0, presentStudentCount - gradedCount);

      return {
        agenda,
        gradedCount,
        presentStudentCount,
        absentStudentCount,
        totalStudents,
        ungradedCount,
        isUngraded,
      };
    }).filter(item => item.isUngraded);
  }, [allTeacherQuizAgendas, students, assessmentGrades, recapValues, attendanceRecords]);

  // Available classes for Quiz dropdown (assigned + any class in agendas)
  const availableQuizClasses = useMemo(() => {
    const set = new Set<string>();
    (activeUser.assignedClasses || []).forEach(c => set.add(c));
    allTeacherQuizAgendas.forEach(a => {
      if (a.class) set.add(a.class);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }, [activeUser.assignedClasses, allTeacherQuizAgendas]);

  // Count quiz agendas per class
  const quizAgendaCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    allTeacherQuizAgendas.forEach(a => {
      if (a.class) {
        const norm = normalizeClass(a.class);
        map[norm] = (map[norm] || 0) + 1;
      }
    });
    return map;
  }, [allTeacherQuizAgendas]);

  // Auto-detect & sync quiz class intelligently
  useEffect(() => {
    if (initialAgenda) {
      const actTypeStr = (initialAgenda.activityType || '').toString().toLowerCase();
      if (initialAgenda.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN || (initialAgenda.activityType as any) === 'Pembelajaran dan Asesmen' || (initialAgenda.activityType as any) === 'KBM dan Asesmen Harian' || actTypeStr.includes('asesmen') || actTypeStr.includes('pemahaman') || actTypeStr.includes('harian')) {
        if (initialAgenda.class) {
          setQuizClass(initialAgenda.class);
          return;
        }
      }
    }

    if (!quizClass && availableQuizClasses.length > 0) {
      // Find a class that has quiz agendas first
      const classWithAgenda = availableQuizClasses.find(c => (quizAgendaCountMap[normalizeClass(c)] || 0) > 0);
      setQuizClass(classWithAgenda || availableQuizClasses[0]);
    }
  }, [availableQuizClasses, quizClass, initialAgenda, quizAgendaCountMap]);

  // Filter agendas for Quiz in quizClass
  const quizAgendasForClass = useMemo(() => {
    if (!quizClass) return [];
    const targetNorm = normalizeClass(quizClass);
    return allTeacherQuizAgendas.filter(a => 
      normalizeClass(a.class) === targetNorm || a.class === quizClass
    );
  }, [allTeacherQuizAgendas, quizClass]);

  // Sync selectedQuizAgendaId when quizAgendasForClass changes
  useEffect(() => {
    if (quizAgendasForClass.length > 0) {
      if (!selectedQuizAgendaId || !quizAgendasForClass.some(a => a.id === selectedQuizAgendaId)) {
        setSelectedQuizAgendaId(quizAgendasForClass[0].id);
      }
    } else {
      setSelectedQuizAgendaId('');
    }
  }, [quizAgendasForClass, selectedQuizAgendaId]);

  // Selected quiz agenda object
  const selectedQuizAgenda = useMemo(() => {
    return quizAgendasForClass.find(a => a.id === selectedQuizAgendaId) || null;
  }, [quizAgendasForClass, selectedQuizAgendaId]);

  // Load scores for selectedQuizAgenda
  useEffect(() => {
    if (selectedQuizAgenda) {
      const existing = assessmentGrades.find(g => g.id === selectedQuizAgenda.id);
      if (existing && existing.scores) {
        setQuizScores(existing.scores);
      } else if (recapValues[selectedQuizAgenda.id]) {
        setQuizScores(recapValues[selectedQuizAgenda.id]);
      } else {
        setQuizScores({});
      }
    } else {
      setQuizScores({});
    }
  }, [selectedQuizAgenda, assessmentGrades, recapValues]);

  // Students for quizClass
  const filteredQuizStudents = useMemo(() => {
    if (!quizClass) return [];
    const targetNorm = normalizeClass(quizClass);
    return (students || [])
      .filter(s => (normalizeClass(s.class) === targetNorm || s.class === quizClass) && s.status === 'Aktif')
      .filter(s => {
        if (!quizSearch.trim()) return true;
        const q = quizSearch.toLowerCase().trim();
        return s.name.toLowerCase().includes(q) || (s.nisn && s.nisn.includes(q)) || (s.nis && s.nis.includes(q));
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [students, quizClass, quizSearch]);

  // Live Stats for selected quiz agenda
  const quizStats = useMemo(() => {
    const totalAll = filteredQuizStudents.length;
    if (totalAll === 0) return { total: 0, presentTotal: 0, absentCount: 0, filled: 0, avg: 0, max: 0, min: 0 };
    
    let filled = 0;
    let sum = 0;
    let max = -1;
    let min = 101;
    let absentCount = 0;
    let presentTotal = 0;

    filteredQuizStudents.forEach(student => {
      const attRecord = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === selectedQuizAgenda?.id);
      const attStatusLower = attRecord?.status?.trim().toLowerCase();
      const isAbsent = attStatusLower && (
        attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
        attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
        attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
        attStatusLower === AttendanceStatus.Izin.toLowerCase()
      );

      if (isAbsent) {
        absentCount++;
      } else {
        presentTotal++;
        const v = quizScores[student.id];
        if (v !== '' && v !== null && v !== undefined) {
          const num = Number(v);
          if (!isNaN(num)) {
            filled++;
            sum += num;
            if (num > max) max = num;
            if (num < min) min = num;
          }
        }
      }
    });

    return {
      total: totalAll,
      presentTotal,
      absentCount,
      filled,
      avg: filled > 0 ? parseFloat((sum / filled).toFixed(1)) : 0,
      max: max === -1 ? 0 : max,
      min: min === 101 ? 0 : min
    };
  }, [filteredQuizStudents, quizScores, attendanceRecords, selectedQuizAgenda]);

  const handleQuizScoreChange = (studentId: string, val: string, autoSaveNow = true) => {
    // Check if student is absent
    const attRecord = attendanceRecords.find(r => r.studentId === studentId && r.agendaId === selectedQuizAgenda?.id);
    const attStatusLower = attRecord?.status?.trim().toLowerCase();
    const isAbsent = attStatusLower && (
      attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
      attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
      attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
      attStatusLower === AttendanceStatus.Izin.toLowerCase()
    );
    if (isAbsent) return;

    let updated: Record<string, number | string>;
    if (val === '') {
      updated = { ...quizScores, [studentId]: '' };
    } else {
      const num = parseFloat(val);
      if (isNaN(num)) {
        updated = { ...quizScores, [studentId]: '' };
      } else {
        const clamped = Math.min(100, Math.max(0, num));
        updated = { ...quizScores, [studentId]: clamped };
      }
    }
    setQuizScores(updated);
    if (autoSaveNow) {
      handleSaveQuizScores(updated, true);
    }
  };

  const handleBatchSetQuizScore = (targetScore: number | '') => {
    const newScores: Record<string, number | string> = {};
    filteredQuizStudents.forEach(student => {
      const attRecord = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === selectedQuizAgenda?.id);
      const attStatusLower = attRecord?.status?.trim().toLowerCase();
      const isAbsent = attStatusLower && (
        attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
        attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
        attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
        attStatusLower === AttendanceStatus.Izin.toLowerCase()
      );
      if (!isAbsent) {
        newScores[student.id] = targetScore;
      }
    });
    const updated = { ...quizScores, ...newScores };
    setQuizScores(updated);
    handleSaveQuizScores(updated, true);
  };

  const handleApplyPasteScores = () => {
    if (!pasteInputText.trim()) return;
    const lines = pasteInputText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const parsedNumbers: number[] = [];

    lines.forEach(line => {
      // Find all number tokens in line
      const numbers = line.match(/\b\d+(\.\d+)?\b/g);
      if (numbers && numbers.length > 0) {
        const lastNum = parseFloat(numbers[numbers.length - 1]);
        if (!isNaN(lastNum)) {
          parsedNumbers.push(Math.min(100, Math.max(0, lastNum)));
        }
      }
    });

    if (parsedNumbers.length === 0) {
      alert('Tidak dapat mendeteksi angka nilai dari teks yang dimasukkan.');
      return;
    }

    const updated: Record<string, number | string> = { ...quizScores };
    let scoreIdx = 0;
    filteredQuizStudents.forEach((student) => {
      const attRecord = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === selectedQuizAgenda?.id);
      const attStatusLower = attRecord?.status?.trim().toLowerCase();
      const isAbsent = attStatusLower && (
        attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
        attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
        attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
        attStatusLower === AttendanceStatus.Izin.toLowerCase()
      );
      if (!isAbsent) {
        if (scoreIdx < parsedNumbers.length) {
          updated[student.id] = parsedNumbers[scoreIdx];
          scoreIdx++;
        }
      } else {
        updated[student.id] = '';
      }
    });

    setQuizScores(updated);
    setShowPasteModal(false);
    setPasteInputText('');
    handleSaveQuizScores(updated, true);
  };

  const handleSaveQuizScores = (customScores?: Record<string, number | string>, isAuto = true) => {
    if (!selectedQuizAgenda) {
      if (!isAuto) alert('Silakan pilih agenda KBM dan Asesmen Harian terlebih dahulu!');
      return;
    }
    const scoresToSave = customScores || quizScores;
    setIsSavingQuiz(true);
    try {
      const cleanScores: Record<string, number> = {};
      Object.entries(scoresToSave).forEach(([sId, scoreVal]) => {
        if (scoreVal !== '' && scoreVal !== null && scoreVal !== undefined) {
          const num = Number(scoreVal);
          if (!isNaN(num)) {
            const attRecord = attendanceRecords.find(r => r.studentId === sId && r.agendaId === selectedQuizAgenda.id);
            const attStatusLower = attRecord?.status?.trim().toLowerCase();
            const isAbsent = attStatusLower && (
              attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
              attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
              attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
              attStatusLower === AttendanceStatus.Izin.toLowerCase()
            );
            if (!isAbsent) {
              cleanScores[sId] = Math.min(100, Math.max(0, num));
            }
          }
        }
      });

      const newGrade: AssessmentGrade = {
        id: selectedQuizAgenda.id,
        class: selectedQuizAgenda.class,
        subject: selectedQuizAgenda.subject || activeUser.subject,
        tpId: selectedQuizAgenda.tpId || 'PEMAHAMAN',
        activityType: ActivityType.PEMBELAJARAN_DAN_ASESMEN,
        assessmentType: 'KBM dan Asesmen Harian',
        date: selectedQuizAgenda.date,
        scores: cleanScores,
        teacherId: activeUser.id
      };

      const exists = assessmentGrades.findIndex(g => g.id === newGrade.id);
      let updated: AssessmentGrade[];
      if (exists >= 0) {
        updated = assessmentGrades.map((g, i) => i === exists ? newGrade : g);
      } else {
        updated = [...assessmentGrades, newGrade];
      }

      onUpdateGrades(updated);

      // Also sync local recapValues for real-time consistency
      setRecapValues(prev => ({
        ...prev,
        [selectedQuizAgenda.id]: cleanScores
      }));

      if (!isAuto) {
        setSaveSuccessMessage(`Nilai tugas harian untuk Agenda (${selectedQuizAgenda.topic || 'KBM dan Asesmen Harian'}) berhasil disimpan!`);
        setShowSaveSuccessModal(true);
        setTimeout(() => setShowSaveSuccessModal(false), 2500);
      }
    } catch (err) {
      console.error('Error saving quiz scores:', err);
    } finally {
      setTimeout(() => setIsSavingQuiz(false), 200);
    }
  };

  // Helper to open Send to Sumatif Modal with smart auto-selection
  const handleOpenSendToSumatifModal = () => {
    if (!selectedQuizAgenda) {
      alert('Silakan pilih agenda KBM dan Asesmen Harian terlebih dahulu!');
      return;
    }

    const totalFilled = filteredQuizStudents.filter(s => {
      const v = quizScores[s.id];
      return v !== '' && v !== null && v !== undefined;
    }).length;

    if (totalFilled === 0) {
      alert('Belum ada nilai siswa yang diisi pada agenda ini. Silakan input nilai siswa terlebih dahulu sebelum mengirim ke Sumatif TP.');
      return;
    }
    
    // Smart preselection: check if selectedQuizAgenda has tpId or tpCode matching objectives
    let initialTpId = '';
    if (selectedQuizAgenda.tpId && objectives.some(o => o.id === selectedQuizAgenda.tpId)) {
      initialTpId = selectedQuizAgenda.tpId;
    } else if (selectedQuizAgenda.tpCode) {
      const match = objectives.find(o => 
        o.code?.toLowerCase().trim() === selectedQuizAgenda.tpCode?.toLowerCase().trim() || 
        o.code?.replace(/^TP\s*/i, '').trim() === selectedQuizAgenda.tpCode?.replace(/^TP\s*/i, '').trim()
      );
      if (match) initialTpId = match.id;
    }
    
    if (!initialTpId && objectives.length > 0) {
      initialTpId = objectives[0].id;
    }
    
    setTargetSumatifTpId(initialTpId);
    setTargetSumatifActivityType(ActivityType.SUMATIF);
    setShowSendToSumatifModal(true);
  };

  // Execute sending quiz scores to Sumatif TP
  const handleExecuteSendToSumatif = async () => {
    if (!selectedQuizAgenda || !targetSumatifTpId || !quizClass) {
      alert('Silakan pilih Tujuan Pembelajaran (TP) terlebih dahulu!');
      return;
    }

    const targetObjective = objectives.find(o => o.id === targetSumatifTpId);
    if (!targetObjective) {
      alert('Tujuan Pembelajaran yang dipilih tidak valid.');
      return;
    }

    setIsSendingToSumatif(true);
    try {
      // 1. Ensure current quiz scores are cleaned and saved first
      await handleSaveQuizScores(quizScores, true);

      const cleanQuizScores: Record<string, number> = {};
      Object.entries(quizScores).forEach(([sId, scoreVal]) => {
        if (scoreVal !== '' && scoreVal !== null && scoreVal !== undefined) {
          const num = Number(scoreVal);
          if (!isNaN(num)) {
            const attRecord = attendanceRecords.find(r => r.studentId === sId && r.agendaId === selectedQuizAgenda.id);
            const attStatusLower = attRecord?.status?.trim().toLowerCase();
            const isAbsent = attStatusLower && (
              attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
              attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
              attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
              attStatusLower === AttendanceStatus.Izin.toLowerCase()
            );
            if (!isAbsent) {
              cleanQuizScores[sId] = Math.min(100, Math.max(0, num));
            }
          }
        }
      });

      const totalSentStudents = Object.keys(cleanQuizScores).length;
      if (totalSentStudents === 0) {
        alert('Belum ada nilai siswa yang valid untuk dikirim.');
        setIsSendingToSumatif(false);
        return;
      }

      // 2. Prepare or merge AssessmentGrade for Sumatif / Praktikum TP
      const targetVirtualId = `${quizClass}::${targetSumatifTpId}::${targetSumatifActivityType}`;
      const existingGradeIdx = assessmentGrades.findIndex(g => 
        g.id === targetVirtualId ||
        (normalizeClass(g.class) === normalizeClass(quizClass) && g.tpId === targetSumatifTpId && g.activityType === targetSumatifActivityType)
      );

      let targetGradeId = targetVirtualId;
      let mergedScores: Record<string, number> = {};

      if (existingGradeIdx >= 0) {
        const existing = assessmentGrades[existingGradeIdx];
        targetGradeId = existing.id;
        mergedScores = { ...(existing.scores || {}), ...cleanQuizScores };
      } else {
        mergedScores = { ...cleanQuizScores };
      }

      const newSumatifGrade: AssessmentGrade = {
        id: targetGradeId,
        class: quizClass,
        subject: selectedQuizAgenda.subject || activeUser.subject,
        tpId: targetSumatifTpId,
        activityType: targetSumatifActivityType,
        assessmentType: targetSumatifActivityType === ActivityType.PRAKTIKUM ? 'Praktikum TP' : 'Sumatif per TP',
        date: selectedQuizAgenda.date || new Date().toISOString().split('T')[0],
        scores: mergedScores,
        teacherId: activeUser.id,
      };

      let updatedAssessmentGrades: AssessmentGrade[];
      if (existingGradeIdx >= 0) {
        updatedAssessmentGrades = assessmentGrades.map((g, idx) => idx === existingGradeIdx ? newSumatifGrade : g);
      } else {
        updatedAssessmentGrades = [...assessmentGrades, newSumatifGrade];
      }

      // 3. Update FormativeGradeTP (summativeScores / praktikumScores)
      const formativeDocId = `${quizClass}-${targetSumatifTpId}`;
      const existingFormativeIdx = (formativeGradesTP || []).findIndex(f => 
        f.id === formativeDocId || (normalizeClass(f.class) === normalizeClass(quizClass) && f.tpId === targetSumatifTpId)
      );

      let updatedFormativeList: FormativeGradeTP[];
      if (existingFormativeIdx >= 0) {
        const currentF = formativeGradesTP[existingFormativeIdx];
        const updatedF: FormativeGradeTP = {
          ...currentF,
          class: quizClass,
          tpId: targetSumatifTpId,
          summativeScores: targetSumatifActivityType === ActivityType.SUMATIF
            ? { ...(currentF.summativeScores || {}), ...cleanQuizScores }
            : currentF.summativeScores,
          praktikumScores: targetSumatifActivityType === ActivityType.PRAKTIKUM
            ? { ...(currentF.praktikumScores || {}), ...cleanQuizScores }
            : currentF.praktikumScores,
          teacherId: activeUser.id
        };
        updatedFormativeList = formativeGradesTP.map((f, i) => i === existingFormativeIdx ? updatedF : f);
      } else {
        const newF: FormativeGradeTP = {
          id: formativeDocId,
          class: quizClass,
          tpId: targetSumatifTpId,
          levels: {},
          summativeScores: targetSumatifActivityType === ActivityType.SUMATIF ? cleanQuizScores : {},
          praktikumScores: targetSumatifActivityType === ActivityType.PRAKTIKUM ? cleanQuizScores : {},
          teacherId: activeUser.id
        };
        updatedFormativeList = [...(formativeGradesTP || []), newF];
      }

      // 4. Update recapValues for live feedback in "Penilaian Sumatif dan Praktikum TP" tab
      setRecapValues(prev => ({
        ...prev,
        [targetGradeId]: mergedScores
      }));

      // 5. Trigger prop updates to persist in Firebase Firestore
      onUpdateGrades(updatedAssessmentGrades);
      onUpdateFormativeTP(updatedFormativeList);

      setShowSendToSumatifModal(false);
      setSaveSuccessMessage(`Berhasil mengirim ${totalSentStudents} nilai Tugas Harian ke ${targetSumatifActivityType === ActivityType.PRAKTIKUM ? 'Praktikum' : 'Sumatif'} ${targetObjective.code} (Kelas ${quizClass})!`);
      setShowSaveSuccessModal(true);
      setTimeout(() => setShowSaveSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error sending grades to Sumatif TP:', error);
      alert('Terjadi kesalahan saat mengirim nilai ke Sumatif TP.');
    } finally {
      setIsSendingToSumatif(false);
    }
  };

  const handleExportNotebookExcel = async () => {
    if (!notebookClass) return;

    const classStudents = students
      .filter(s => s.class === notebookClass && s.status === 'Aktif')
      .sort((a, b) => a.name.localeCompare(b.name));

    if (classStudents.length === 0) return;

    // Calculate maxTtd for calculation of Nilai Catatan
    let maxTtd = 0;
    classStudents.forEach(student => {
      const entry = notebookEntries[student.id] || { jumlahTtd: 0, notes: '' };
      const ttd = entry.jumlahTtd || 0;
      if (ttd > maxTtd) {
        maxTtd = ttd;
      }
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rekap Buku Catatan');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    await addLogoToWorksheet(workbook, worksheet);

    worksheet.mergeCells('B1:F1');
    worksheet.getCell('B1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('B1').font = { bold: true, size: 12 };
    worksheet.getCell('B1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B2:F2');
    worksheet.getCell('B2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('B2').font = { bold: true, size: 12 };
    worksheet.getCell('B2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B3:F3');
    worksheet.getCell('B3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('B3').font = { bold: true, size: 16 };
    worksheet.getCell('B3').alignment = { horizontal: 'center' };

    const address = schoolSettings.address || '';
    const addressParts = address.split('\n');
    if (addressParts.length >= 2) {
      worksheet.mergeCells('B4:F4');
      worksheet.getCell('B4').value = addressParts[0];
      worksheet.getCell('B4').font = { size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };
      
      worksheet.mergeCells('B5:F5');
      worksheet.getCell('B5').value = addressParts[1] + (schoolSettings.postalCode ? ` Kode Pos ${schoolSettings.postalCode}` : '');
      worksheet.getCell('B5').font = { size: 10 };
      worksheet.getCell('B5').alignment = { horizontal: 'center' };
    } else {
      worksheet.mergeCells('B4:F4');
      worksheet.getCell('B4').value = address;
      worksheet.getCell('B4').font = { size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };
    }

    worksheet.getCell('A6').border = { bottom: { style: 'double' } };
    worksheet.getCell('B6').border = { bottom: { style: 'double' } };
    worksheet.getCell('C6').border = { bottom: { style: 'double' } };
    worksheet.getCell('D6').border = { bottom: { style: 'double' } };
    worksheet.getCell('E6').border = { bottom: { style: 'double' } };
    worksheet.getCell('F6').border = { bottom: { style: 'double' } };

    worksheet.mergeCells('A8:F8');
    worksheet.getCell('A8').value = 'REKAPITULASI PEMERIKSAAN BUKU CATATAN SISWA';
    worksheet.getCell('A8').font = { bold: true, size: 14 };
    worksheet.getCell('A8').alignment = { horizontal: 'center' };

    worksheet.addRow([]);
    worksheet.addRow(['', 'Mata Pelajaran', ':', activeUser.subject]);
    worksheet.addRow(['', 'Nama Guru', ':', activeUser.name]);
    worksheet.addRow(['', 'Kelas', ':', notebookClass]);
    worksheet.addRow([]);

    const headerRow = worksheet.addRow(['NO', 'NAMA SISWA', 'NISN / NIS', 'JUMLAH TTD', 'NILAI CATATAN', 'KETERANGAN']);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
      cell.font = { bold: true };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    classStudents.forEach((student, idx) => {
      const entry = notebookEntries[student.id] || { jumlahTtd: 0, notes: '' };
      const ttd = entry.jumlahTtd || 0;
      const nilaiCatatan = maxTtd > 0 ? Math.round((ttd / maxTtd) * 100) : 0;

      const dataRow = worksheet.addRow([
        idx + 1,
        student.name,
        student.nisn || student.nis || '',
        ttd,
        nilaiCatatan,
        entry.notes || '-'
      ]);

      dataRow.eachCell((cell, colNumber) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        if (colNumber !== 2 && colNumber !== 6) {
          cell.alignment = { horizontal: 'center' };
        }
      });
    });

    const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', `Tanggal: ${dateStr}`]);
    worksheet.addRow(['', '', '', '', 'Guru Mata Pelajaran,']);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', activeUser.name]);

    worksheet.getColumn(1).width = 6;  // NO
    worksheet.getColumn(2).width = 34; // NAMA
    worksheet.getColumn(3).width = 18; // NISN
    worksheet.getColumn(4).width = 16; // JUMLAH TTD
    worksheet.getColumn(5).width = 16; // NILAI CATATAN
    worksheet.getColumn(6).width = 32; // KETERANGAN

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
      FileSaver.saveAs(blob, `REKAP_BUKU_CATATAN_${activeUser.subject}_KELAS_${notebookClass}_${new Date().toISOString().split('T')[0]}.xlsx`);
    });
  };

  // Auto-save logic
  /*
  useEffect(() => {
    if (activeTab !== 'rekap') return;

    const autoSaveInterval = setInterval(() => {
      handleSaveRecap();
    }, 118000); // 118 seconds (2 seconds before 120s refresh)

    return () => clearInterval(autoSaveInterval);
  }, [activeTab, recapValues, assessmentGrades, rekapClass]);
  */

  const handleDeleteAssessment = (id: string) => {
    const updated = (assessmentGrades || []).filter(g => g.id !== id);
    onUpdateGrades(updated);
    setShowConfirmModal(false);
    setAssessmentToDelete(null);
  };



  const lastRekapClassRef = useRef<string | null>(null);

  // Initialize recapValues when rekapClass or assessmentGrades change
  useEffect(() => {
    if (rekapClass) {
      const initialValues: Record<string, Record<string, number>> = {};
      
      assessmentGrades.forEach(ass => {
        if (ass.class === rekapClass || ass.class === 'Semua Kelas') {
          initialValues[ass.id] = { ...(initialValues[ass.id] || {}), ...(ass.scores || {}) };
        }
      });

      if (lastRekapClassRef.current !== rekapClass) {
          setRecapValues(initialValues);
          lastRekapClassRef.current = rekapClass;
      } else {
          setRecapValues(prev => {
              const merged = { ...prev };
              Object.keys(initialValues).forEach(id => {
                  merged[id] = { ...(initialValues[id] || {}), ...(prev[id] || {}) };
              });
              return merged;
          });
      }
    }
  }, [rekapClass, assessmentGrades]);

  const handleRecapScoreChange = (assId: string, studentId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    const clampedValue = Math.min(100, Math.max(0, numValue));
    
    setRecapValues(prev => ({
      ...prev,
      [assId]: {
        ...(prev[assId] || {}),
        [studentId]: clampedValue
      }
    }));
  };

  const handleSaveRecap = (isAuto = false) => {
    if (!rekapClass) return;

    // We need to update assessmentGrades based on recapValues
    let updatedGrades = [...assessmentGrades];
    
    Object.entries(recapValues).forEach(([assId, scores]) => {
      const existingIndex = updatedGrades.findIndex(g => g.id === assId);
      const typedScores = scores as Record<string, number>;
      
      if (existingIndex >= 0) {
        // Update existing
        updatedGrades[existingIndex] = {
          ...updatedGrades[existingIndex],
          scores: { 
            ...(updatedGrades[existingIndex].scores || {}),
            ...typedScores 
          }
        };
      } else {
        // Create new (for virtual columns)
        const parts = assId.split('::');
        if (parts.length === 3) {
            const [cls, tpId, activityType] = parts;
            
            const newGrade: AssessmentGrade = {
              id: assId,
              class: cls,
              tpId: tpId,
              activityType: activityType as ActivityType,
              assessmentType: 'Sumatif per TP',
              date: new Date().toISOString().split('T')[0],
              scores: { ...typedScores }
            };
            updatedGrades.push(newGrade);
        }
      }
    });

    onUpdateGrades(updatedGrades);
    if (!isAuto) {
      setShowSaveConfirmModal(false);
      setSaveSuccessMessage('Rekapitulasi nilai telah berhasil diperbarui.');
      setShowSaveSuccessModal(true);
      setTimeout(() => setShowSaveSuccessModal(false), 2000);
    }
  };

  // Auto-save effect for Recap
  /*
  useEffect(() => {
    if (!rekapClass) return;
    const timer = setTimeout(() => {
      handleSaveRecap(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [recapValues, rekapClass]);
  */

  // Effect for Sumatif Akhir
  useEffect(() => {
    if (activeTab === 'sumatif-akhir' && sasClass) {
      const classGrades = (sasGrades || []).filter(g => g.class === sasClass && g.subject === activeUser.subject);
      if (classGrades.length > 0) {
        const scores: Record<string, number> = {};
        classGrades.forEach(g => {
          scores[g.studentId] = g.score;
        });
        setSasScores(scores);
      } else {
        setSasScores({});
      }
    }
  }, [activeTab, sasClass, sasGrades, activeUser.subject]);

  const handleSasScoreChange = (studentId: string, value: string) => {
    if (value === '') {
      setSasScores(prev => {
        const next = { ...prev };
        delete next[studentId];
        return next;
      });
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return;
    const clamped = Math.min(100, Math.max(0, num));
    setSasScores(prev => ({ ...prev, [studentId]: clamped }));
  };

  const handleSaveSas = async (isAuto = false) => {
    if (!sasClass) return;
    setIsSavingSas(true);

    try {
      const date = new Date().toISOString().split('T')[0];
      const studentMap = new Map(sortedSasStudents.map(s => [s.id, s]));

      const newSasGrades: SasGrade[] = Object.entries(sasScores).map(([studentId, score]: [string, number]) => {
        const studentInfo = studentMap.get(studentId);
        const nilaiTh = studentInfo?.nilaiTh ?? 0;
        const nilaiCatatan = studentInfo?.nilaiCatatan ?? 0;
        const scoreNum = Number(score) || 0;
        let nilaiEvaluasi = Math.round((((nilaiTh + nilaiCatatan) / 2) * (weightDaily / 100)) + (scoreNum * (weightSas / 100)));
        if (nilaiEvaluasi < scoreNum) {
          nilaiEvaluasi = Math.min(100, scoreNum + 1);
        }

        const existing = sasGrades.find(g => 
          g.studentId === studentId && 
          g.subject === activeUser.subject && 
          g.class === sasClass
        );

        return {
          id: existing?.id || `${studentId}-${activeUser.subject}-${date}`,
          studentId,
          score,
          nilaiEvaluasi,
          nilaiTh,
          nilaiCatatan,
          class: sasClass,
          subject: activeUser.subject,
          teacherId: activeUser.id,
          date
        };
      });

      // Merge with existing grades from other classes/subjects
      const otherGrades = (sasGrades || []).filter(g => 
        !(g.class === sasClass && g.subject === activeUser.subject)
      );
      
      const updated = [...otherGrades, ...newSasGrades];

      console.log('Saving SAS & Evaluasi Grades to Firebase:', newSasGrades);
      await onUpdateSasGrades(updated);
      console.log('SAS & Evaluasi Grades saved successfully to Firebase');
      
      if (!isAuto) {
        setSaveSuccessMessage('Penilaian Sumatif Akhir berhasil disimpan!');
        setShowSaveSuccessModal(true);
        setTimeout(() => {
          setShowSaveSuccessModal(false);
          setIsSavingSas(false);
        }, 3000);
      } else {
        setIsSavingSas(false);
      }
    } catch (error) {
      console.error('Error saving SAS:', error);
      setIsSavingSas(false);
      if (!isAuto) alert('Gagal menyimpan penilaian Sumatif Akhir: ' + error);
    }
  };

  // Auto-save effect for SAS dihilangkan (manual saja)
  /*
  useEffect(() => {
    if (activeTab === 'sumatif-akhir' && sasClass) {
      const timer = setTimeout(() => {
        handleSaveSas(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sasScores, sasClass, activeTab]);
  */

  const monthNames = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];


  // Deep Link Handling dari Dashboard
  useEffect(() => {
    if (initialAgenda) {
      const actTypeStr = (initialAgenda.activityType || '').toString().toLowerCase();
      if (initialAgenda.activityType === ActivityType.KBM_DAN_QUIZ || actTypeStr.includes('quiz') || actTypeStr.includes('kuis')) {
        setActiveTab('kuis');
        setQuizClass(initialAgenda.class);
        setSelectedQuizAgendaId(initialAgenda.id);
      } else {
        setActiveTab('sumatif');
        handleSelectAssessment(initialAgenda);
      }
    }
  }, [initialAgenda]);

  // Filter agendas yang memiliki aktivitas penilaian dan sudah selesai
  const availableAssessments = useMemo(() => {
    let filtered = (agendas || []).filter(a => 
      activeUser.assignedClasses.includes(a.class) &&
      a.status === 'Selesai' && 
      a.activityType && 
      [ActivityType.KBM_DAN_QUIZ, ActivityType.SAS, ActivityType.SUMATIF].includes(a.activityType)
    );

    if (classFilter !== 'SEMUA') {
      filtered = filtered.filter(a => a.class === classFilter);
    }
    if (typeFilter !== 'SEMUA') {
      filtered = filtered.filter(a => a.activityType === typeFilter);
    }
    if (statusFilter !== 'SEMUA') {
      filtered = filtered.filter(a => {
        const isGraded = assessmentGrades.some(g => g.id === a.id);
        return statusFilter === 'SUDAH' ? isGraded : !isGraded;
      });
    }
    if (tpFilter !== 'SEMUA') {
        filtered = filtered.filter(a => a.tpId === tpFilter);
    }
    if (startDateFilter) {
        filtered = filtered.filter(a => a.date >= startDateFilter);
    }
    if (endDateFilter) {
        filtered = filtered.filter(a => a.date <= endDateFilter);
    }

    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [agendas, assessmentGrades, classFilter, typeFilter, statusFilter, tpFilter, startDateFilter, endDateFilter, activeUser.assignedClasses]);

  const allClasses = useMemo(() => {
    return Array.from(new Set(students.map(s => s.class))).sort();
  }, [students]);

  const studentAttendanceStats = useMemo(() => {
    const statsMap: Record<string, { hadir: number, alpa: number, sakit: number, izin: number, dispen: number, percentage: number, total: number }> = {};
    
    const teacherAgendasIds = new Set(agendas.filter(a => 
      (a.teacherId === activeUser.id || a.teacherId === activeUser.username) &&
      (a.subject?.trim().toLowerCase() === activeUser.subject?.trim().toLowerCase())
    ).map(a => a.id));

    students.forEach(student => {
      const studentAttendance = attendanceRecords.filter(r => 
        r.studentId === student.id && 
        (teacherAgendasIds.has(r.agendaId) || r.teacherId === activeUser.id || r.teacherId === activeUser.username)
      );
      
      const total = studentAttendance.length;
      let hadir = 0, alpa = 0, sakit = 0, izin = 0, dispen = 0;

      studentAttendance.forEach(r => {
        const s = r.status?.trim().toLowerCase();
        if (s === AttendanceStatus.Hadir.toLowerCase() || s === AttendanceStatus.Terlambat.toLowerCase() || s === AttendanceStatus.Pulang.toLowerCase()) hadir++;
        else if (s === AttendanceStatus.Alpa.toLowerCase()) alpa++;
        else if (s === AttendanceStatus.Sakit.toLowerCase()) sakit++;
        else if (s === AttendanceStatus.Izin.toLowerCase()) izin++;
        else if (s === AttendanceStatus.Dispen.toLowerCase()) dispen++;
      });
      
      statsMap[student.id] = {
        hadir,
        alpa,
        sakit,
        izin,
        dispen,
        total,
        percentage: total > 0 ? Math.round((hadir / total) * 100) : 0
      };
    });
    
    return statsMap;
  }, [students, attendanceRecords, agendas, activeUser.id, activeUser.username, activeUser.subject]);

  const sasNotebookData = useMemo(() => {
    if (!sasClass || !activeUser?.id) return { entries: {}, maxTtd: 0 };
    try {
      const storageKey = `smpn1_notebook_rekap_${activeUser.id}_${sasClass}`;
      const saved = localStorage.getItem(storageKey);
      let entries: Record<string, { jumlahTtd: number; notes: string }> = {};
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          if (parsed.entries) entries = parsed.entries;
          else entries = parsed;
        }
      }
      
      const classStudents = students.filter(s => s.class === sasClass && s.status === 'Aktif');
      let maxTtd = 0;
      classStudents.forEach(student => {
        const entry = entries[student.id] || { jumlahTtd: 0, notes: '' };
        const ttd = entry.jumlahTtd || 0;
        if (ttd > maxTtd) {
          maxTtd = ttd;
        }
      });
      
      return { entries, maxTtd };
    } catch (err) {
      console.error('Error loading notebook data for sasClass:', err);
      return { entries: {}, maxTtd: 0 };
    }
  }, [sasClass, activeUser?.id, students, notebookEntries]);

  const sortedSasStudents = useMemo(() => {
    if (!sasClass) return [];
    
    const classStudents = students.filter(s => s.class === sasClass && s.status === 'Aktif');
    
    // Get all quiz / tugas harian assessments for this class
    const classQuizGrades = assessmentGrades.filter(g => 
      normalizeClass(g.class) === normalizeClass(sasClass) && 
      ((g.activityType as any) === 'KBM dan Quiz' || 
       (g.activityType as any) === 'Pembelajaran dan Asesmen' || 
       (g.activityType as any) === 'KBM dan Asesmen Harian' || 
       g.activityType === ActivityType.KBM_DAN_QUIZ || 
       g.activityType === ActivityType.KBM)
    );
    const totalQuizAgendas = classQuizGrades.length;

    const { entries: sasNotebookEntries, maxTtd: sasNotebookMaxTtd } = sasNotebookData;
    
    const studentsWithData = classStudents.map(student => {
        let sumQuizScores = 0;
        classQuizGrades.forEach(g => {
          const score = g.scores[student.id];
          const num = Number(score);
          if (!isNaN(num) && score !== undefined && score !== null && String(score) !== '') {
            sumQuizScores += num;
          }
        });
        const nilaiTh = totalQuizAgendas > 0 ? Math.round(sumQuizScores / totalQuizAgendas) : 0;

        // Calculate Nilai Catatan
        const ttd = sasNotebookEntries[student.id]?.jumlahTtd || 0;
        const nilaiCatatan = sasNotebookMaxTtd > 0 ? Math.round((ttd / sasNotebookMaxTtd) * 100) : 0;

        const score = sasScores[student.id] ?? 0;
        // Formula: (((Nilai TH + Nilai Catatan)/2) * weightDaily%) + (Nilai Sumatif Akhir * weightSas%)
        let nilaiEvaluasi = Math.round((((nilaiTh + nilaiCatatan) / 2) * (weightDaily / 100)) + (score * (weightSas / 100)));
        if (nilaiEvaluasi < score) {
          nilaiEvaluasi = Math.min(100, score + 1);
        }

        return {
            ...student,
            score,
            percentage: studentAttendanceStats[student.id]?.percentage ?? 0,
            alpa: studentAttendanceStats[student.id]?.alpa ?? 0,
            sakit: studentAttendanceStats[student.id]?.sakit ?? 0,
            izin: studentAttendanceStats[student.id]?.izin ?? 0,
            dispen: studentAttendanceStats[student.id]?.dispen ?? 0,
            nilaiTh,
            nilaiCatatan,
            nilaiEvaluasi
        };
    });

    return [...studentsWithData].sort((a, b) => {
        const { key, direction } = sasSortConfig;
        let valA: any = a[key];
        let valB: any = b[key];

        if (key === 'name') {
            const cmp = valA.localeCompare(valB);
            return direction === 'asc' ? cmp : -cmp;
        }

        // Secondary sort by name if numeric values are equal
        if (valA === valB) {
            return a.name.localeCompare(b.name);
        }

        return direction === 'asc' ? valA - valB : valB - valA;
    });
  }, [students, sasClass, sasScores, sasSortConfig, studentAttendanceStats, assessmentGrades, sasNotebookData, weightDaily, weightSas]);

  const handleSasSort = (key: 'name' | 'score' | 'percentage' | 'alpa' | 'nilaiTh' | 'nilaiCatatan' | 'nilaiEvaluasi') => {
    setSasSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const summativeAssessments = useMemo(() => {
    const summativeTPs = (objectives || []).filter(o => o.isSummative);
    const assessments: any[] = [];
    allClasses.forEach(cls => {
        if (classFilter !== 'SEMUA' && classFilter !== cls) return;
        summativeTPs.forEach(tp => {
            if (tpFilter !== 'SEMUA' && tpFilter !== tp.id) return;
            assessments.push({
                id: `${cls}::${tp.id}::${ActivityType.SUMATIF}`,
                class: cls,
                tpId: tp.id,
                activityType: ActivityType.SUMATIF,
                date: new Date().toISOString().split('T')[0],
                tp: tp
            });
        });
    });
    return assessments;
  }, [objectives, allClasses, classFilter, tpFilter]);

  const praktikumAssessments = useMemo(() => {
    const praktikumTPs = (objectives || []).filter(o => o.isPracticum);
    const assessments: any[] = [];
    allClasses.forEach(cls => {
        if (classFilter !== 'SEMUA' && classFilter !== cls) return;
        praktikumTPs.forEach(tp => {
            if (tpFilter !== 'SEMUA' && tpFilter !== tp.id) return;
            assessments.push({
                id: `${cls}::${tp.id}::${ActivityType.PRAKTIKUM}`,
                class: cls,
                tpId: tp.id,
                activityType: ActivityType.PRAKTIKUM,
                date: new Date().toISOString().split('T')[0],
                tp: tp
            });
        });
    });
    return assessments;
  }, [objectives, allClasses, classFilter, tpFilter]);

  const allAssessments = useMemo(() => {
    let assessments = [...availableAssessments];
    if (typeFilter === 'SEMUA' || typeFilter === ActivityType.SUMATIF) {
        assessments = [...assessments, ...summativeAssessments];
    }
    if (typeFilter === 'SEMUA' || typeFilter === ActivityType.PRAKTIKUM) {
        assessments = [...assessments, ...praktikumAssessments];
    }
    return assessments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [availableAssessments, summativeAssessments, praktikumAssessments, typeFilter]);

  // SUMATIF LOGIC
  const filteredStudentsForSelectedClass = useMemo(() => {
    if (!selectedAssessment) return [];
    return (students || []).filter(s => s.class === selectedAssessment.class && s.status === 'Aktif');
  }, [students, selectedAssessment]);

  const handleSelectAssessment = (item: any) => {
    setSelectedAssessment(item);
    
    // Check recapValues first (unsaved changes in Rekap tab)
    if (recapValues[item.id]) {
      setScores(recapValues[item.id]);
    } else {
      const existing = assessmentGrades.find(g => g.id === item.id);
      if (existing) setScores(existing.scores);
      else setScores({});
    }
  };

  const referenceData = useMemo(() => {
    if (!selectedAssessment) return {};
    
    const tpId = selectedAssessment.tpId;
    const cls = selectedAssessment.class;
    
    const data: Record<string, { otherScores: any[], average: number }> = {};
    
    filteredStudentsForSelectedClass.forEach(student => {
      let total = 0;
      let count = 0;
      const others: any[] = [];
      
      // 1. Check assessmentGrades (saved data)
      assessmentGrades.forEach(g => {
        if (g.tpId === tpId && g.class === cls) {
          let score: number | string | undefined = g.scores[student.id];
          // Override with recapValues if available
          if (recapValues[g.id]?.[student.id] !== undefined) {
            score = recapValues[g.id][student.id];
          }

          if (score !== undefined && score !== null && (score as any) !== '') {
            const numScore = Number(score);
            total += numScore;
            count++;
            if (g.id !== selectedAssessment.id) {
              others.push({ id: g.id, score: numScore, label: g.activityType });
            }
          }
        }
      });

      // 2. Check recapValues for virtual IDs not in assessmentGrades
      Object.entries(recapValues).forEach(([assId, scores]) => {
        if (assId.includes('::')) {
          const [vCls, vTpId, vType] = assId.split('::');
          if (vCls === cls && vTpId === tpId) {
            const score: number | string | undefined = scores[student.id];
            if (score !== undefined && score !== null && (score as any) !== '') {
              // Avoid double counting
              if (!assessmentGrades.some(g => g.id === assId)) {
                const numScore = Number(score);
                total += numScore;
                count++;
                if (assId !== selectedAssessment.id) {
                  others.push({ id: assId, score: numScore, label: vType });
                }
              }
            }
          }
        }
      });
      
      data[student.id] = {
        otherScores: others,
        average: count > 0 ? Math.round(total / count) : 0
      };
    });
    
    return data;
  }, [selectedAssessment, assessmentGrades, filteredStudentsForSelectedClass, recapValues]);

  // FORMATIF TP LOGIC
  const formativeLevels: FormativeLevel[] = ['Awal Berkembang', 'Layak', 'Mahir'];

  const filteredStudentsForFormative = useMemo(() => {
    if (!selectedClass) return [];
    return (students || []).filter(s => s.class === selectedClass && s.status === 'Aktif').sort((a,b) => a.name.localeCompare(b.name));
  }, [students, selectedClass]);

  const formativeReferenceData = useMemo(() => {
    if (!selectedClass || !selectedTP) return {};
    
    const data: Record<string, { sumatifAvg: number, praktikumAvg: number }> = {};
    
    filteredStudentsForFormative.forEach(student => {
      let sumTotal = 0;
      let sumCount = 0;
      let prakTotal = 0;
      let prakCount = 0;
      
      // Check assessmentGrades and recapValues
      assessmentGrades.forEach(g => {
        if (g.tpId === selectedTP && g.class === selectedClass) {
          let score: number | string | undefined = g.scores[student.id];
          if (recapValues[g.id]?.[student.id] !== undefined) {
            score = recapValues[g.id][student.id];
          }

          if (score !== undefined && score !== null && (score as any) !== '') {
            const numScore = Number(score);
            if (g.activityType === ActivityType.SUMATIF) {
              sumTotal += numScore;
              sumCount++;
            } else if (g.activityType === ActivityType.PRAKTIKUM) {
              prakTotal += numScore;
              prakCount++;
            }
          }
        }
      });

      // Check virtual IDs in recapValues
      Object.entries(recapValues).forEach(([assId, scores]) => {
        if (assId.includes('::')) {
          const [vCls, vTpId, vType] = assId.split('::');
          if (vCls === selectedClass && vTpId === selectedTP) {
            const score: number | string | undefined = scores[student.id];
            if (score !== undefined && score !== null && (score as any) !== '') {
              if (!assessmentGrades.some(g => g.id === assId)) {
                const numScore = Number(score);
                if (vType === ActivityType.SUMATIF) {
                  sumTotal += numScore;
                  sumCount++;
                } else if (vType === ActivityType.PRAKTIKUM) {
                  prakTotal += numScore;
                  prakCount++;
                }
              }
            }
          }
        }
      });
      
      data[student.id] = {
        sumatifAvg: sumCount > 0 ? Math.round(sumTotal / sumCount) : 0,
        praktikumAvg: prakCount > 0 ? Math.round(prakTotal / prakCount) : 0
      };
    });
    
    return data;
  }, [selectedClass, selectedTP, assessmentGrades, filteredStudentsForFormative, recapValues]);

  const handleScoreChange = (studentId: string, score: string) => {
    if (score === '') {
      setScores(prev => ({ ...prev, [studentId]: '' }));
      return;
    }
    const num = parseFloat(score);
    if (num > 100) return; // Batasi nilai maksimal 100
    setScores(prev => ({ ...prev, [studentId]: isNaN(num) ? '' : num }));
  };

  const handleSaveScores = () => {
    if (!selectedAssessment) return;
    
    // Convert empty strings to 0 or remove them before saving
    const cleanScores: Record<string, number> = {};
    Object.entries(scores).forEach(([id, val]) => {
      if (val !== '') {
        cleanScores[id] = Number(val);
      }
    });

    const newGrade: AssessmentGrade = {
        id: selectedAssessment.id,
        class: selectedAssessment.class,
        tpId: selectedAssessment.tpId || '',
        activityType: selectedAssessment.activityType!,
        assessmentType: assessmentType,
        date: selectedAssessment.date,
        scores: cleanScores
    };
    const exists = assessmentGrades.findIndex(g => g.id === newGrade.id);
    let updated;
    if (exists >= 0) updated = assessmentGrades.map((g, i) => i === exists ? newGrade : g);
    else updated = [...assessmentGrades, newGrade];
    onUpdateGrades(updated);
    alert('Nilai sumatif berhasil disimpan!');
    setSelectedAssessment(null);
  };

  const getLevelColor = (level: FormativeLevel) => {
    switch (level) {
      case 'Awal Berkembang': return 'bg-rose-600 text-white border-rose-600 shadow-rose-500/20';
      case 'Layak': return 'bg-orange-500 text-white border-orange-500 shadow-orange-500/20';
      case 'Mahir': return 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-500/20';
      default: return 'bg-slate-100 text-slate-400 border-slate-200';
    }
  };

  useEffect(() => {
    if (selectedClass && selectedTP) {
        const existing = formativeGradesTP.find(g => g.class === selectedClass && g.tpId === selectedTP);
        let levels = {};
        let pScores = {};
        let sScores = {};

        if (existing) {
            // Defensive parsing in case data was stored as stringified JSON
            levels = typeof existing.levels === 'string' ? JSON.parse(existing.levels) : (existing.levels || {});
            // Migrate 'Cakap' to 'Mahir'
            for (const key in levels) {
                if (levels[key] === 'Cakap') {
                    levels[key] = 'Mahir';
                }
            }
            pScores = typeof (existing as any).praktikumScores === 'string' ? JSON.parse((existing as any).praktikumScores) : ((existing as any).praktikumScores || {});
            sScores = typeof (existing as any).summativeScores === 'string' ? JSON.parse((existing as any).summativeScores) : ((existing as any).summativeScores || {});
        }

        // Pre-fill with reference data if empty
        const finalSScores = { ...sScores };
        const finalPScores = { ...pScores };

        filteredStudentsForFormative.forEach(student => {
            if (finalSScores[student.id] === undefined || finalSScores[student.id] === null || finalSScores[student.id] === 0 || finalSScores[student.id] === '') {
                if (formativeReferenceData[student.id]?.sumatifAvg > 0) {
                    finalSScores[student.id] = formativeReferenceData[student.id].sumatifAvg;
                }
            }
            if (finalPScores[student.id] === undefined || finalPScores[student.id] === null || finalPScores[student.id] === 0 || finalPScores[student.id] === '') {
                if (formativeReferenceData[student.id]?.praktikumAvg > 0) {
                    finalPScores[student.id] = formativeReferenceData[student.id].praktikumAvg;
                }
            }
        });
        
        setFormativeValues(levels);
        setPraktikumValues(finalPScores);
        setSummativeValues(finalSScores);
    }
  }, [selectedClass, selectedTP, formativeGradesTP, formativeReferenceData, filteredStudentsForFormative]);

  const [isSavingFormative, setIsSavingFormative] = useState(false);

  const handleLevelChange = (studentId: string, level: FormativeLevel) => {
    setFormativeValues(prev => ({ ...prev, [studentId]: level }));
  };

  const handlePraktikumChange = (studentId: string, score: string) => {
    const num = parseFloat(score) || 0;
    const clamped = Math.min(100, Math.max(0, num));
    setPraktikumValues(prev => ({ ...prev, [studentId]: clamped }));
  };

  const handleSummativeChange = (studentId: string, score: string) => {
    const num = parseFloat(score) || 0;
    const clamped = Math.min(100, Math.max(0, num));
    setSummativeValues(prev => ({ ...prev, [studentId]: clamped }));
  };

  const handleSaveFormative = (isAuto = false) => {
    if (!selectedClass || !selectedTP) return;
    
    // Pastikan semua siswa mendapatkan nilai (default 'Layak' jika kosong)
    const finalizedLevels: Record<string, FormativeLevel> = {};
    filteredStudentsForFormative.forEach(student => {
      finalizedLevels[student.id] = formativeValues[student.id] || 'Layak';
    });

    const newFormative: FormativeGradeTP = {
        id: `${selectedClass}-${selectedTP}`,
        class: selectedClass,
        tpId: selectedTP,
        levels: finalizedLevels,
        summativeScores: summativeValues,
        praktikumScores: praktikumValues,
        teacherId: activeUser.id
    };
    
    const exists = formativeGradesTP.findIndex(g => g.id === newFormative.id);
    
    // Check if data actually changed to avoid unnecessary updates
    if (exists >= 0 && isAuto) {
        const current = formativeGradesTP[exists];
        const isSame = JSON.stringify(current.levels) === JSON.stringify(finalizedLevels) &&
                       JSON.stringify((current as any).summativeScores) === JSON.stringify(summativeValues) &&
                       JSON.stringify((current as any).praktikumScores) === JSON.stringify(praktikumValues);
        if (isSame) return;
    }

    let updated;
    if (exists >= 0) updated = formativeGradesTP.map((g, i) => i === exists ? newFormative : g);
    else updated = [...formativeGradesTP, newFormative];
    
    onUpdateFormativeTP(updated);
    if (!isAuto) {
        setSaveSuccessMessage('Penilaian formatif TP berhasil disimpan!');
        setShowSaveSuccessModal(true);
        setTimeout(() => setShowSaveSuccessModal(false), 2000);
    } else {
        setIsSavingFormative(true);
        setTimeout(() => setIsSavingFormative(false), 2000);
    }
  };

  // Auto-save effect
  /*
  useEffect(() => {
    if (!selectedClass || !selectedTP) return;
    
    const timer = setTimeout(() => {
        handleSaveFormative(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [formativeValues, summativeValues, praktikumValues, selectedClass, selectedTP]);
  */
  
  // REKAP NILAI LOGIC
  const recapData = useMemo(() => {
    if (!rekapClass) return { columns: [], rows: [] };

    const classStudents = students.filter(s => s.class === rekapClass && s.status === 'Aktif');
    
    // 1. Get all existing assessments for this class (excluding KBM dan Asesmen Harian)
    let existingAssessments = assessmentGrades.filter(g => {
      const isCorrectClass = g.class === rekapClass || g.class === 'Semua Kelas';
      const isKBM = g.activityType === ActivityType.PEMBELAJARAN_DAN_ASESMEN ||
                    (g.activityType as any) === 'Pembelajaran dan Asesmen' ||
                    (g.activityType as any) === 'KBM dan Asesmen Harian' ||
                    g.activityType === ActivityType.KBM_DAN_QUIZ ||
                    g.activityType === ActivityType.KBM ||
                    (g.assessmentType as any) === 'Pembelajaran dan Asesmen' ||
                    (g.assessmentType as any) === 'KBM dan Asesmen Harian';
      return isCorrectClass && !isKBM;
    });

    if (rekapAssessmentType !== 'Semua') {
        const typeMap: Record<string, string> = {
            'Sumatif per TP': 'Sumatif per TP',
            'Praktikum TP': 'Praktikum TP',
            'SAS': 'Sumatif Akhir'
        };
        const targetType = typeMap[rekapAssessmentType] || rekapAssessmentType;
        
        existingAssessments = existingAssessments.filter(g => {
            if (g.assessmentType) {
                if (rekapAssessmentType === 'Sumatif per TP') return g.assessmentType === 'Sumatif per TP' || g.activityType === ActivityType.SUMATIF;
                if (rekapAssessmentType === 'Praktikum TP') return g.assessmentType === 'Praktikum TP' || g.activityType === ActivityType.PRAKTIKUM;
                return g.assessmentType === targetType;
            }
            // Fallback for older records or records without assessmentType
            if (rekapAssessmentType === 'Sumatif per TP') return g.activityType === ActivityType.SUMATIF;
            if (rekapAssessmentType === 'Praktikum TP') return g.activityType === ActivityType.PRAKTIKUM;
            if (rekapAssessmentType === 'SAS') return g.activityType === ActivityType.SAS;
            return false;
        });
    }
    
    // De-duplicate existingAssessments by ass.id (to prevent duplicates with identical ID)
    const uniqueAssessments = new Map<string, AssessmentGrade>();
    existingAssessments.forEach(ass => {
        if (!uniqueAssessments.has(ass.id)) {
            uniqueAssessments.set(ass.id, ass);
        } else {
            const existing = uniqueAssessments.get(ass.id)!;
            const mergedScores = { ...existing.scores, ...ass.scores };
            uniqueAssessments.set(ass.id, { ...existing, scores: mergedScores });
        }
    });
    existingAssessments = Array.from(uniqueAssessments.values());
    
    // 2. Get all summative and practicum TPs
    const summativeTPs = (objectives || []).filter(o => o.isSummative);
    const practicumTPs = (objectives || []).filter(o => o.isPracticum);

    // 3. Build columns
    const columns: any[] = [];
    
    // Add existing assessments
    existingAssessments.forEach(ass => {
        const tp = objectives.find(o => o.id === ass.tpId);
        if (ass.activityType !== ActivityType.SAS) {
            // Only show TP that exists in teacher's Tujuan Pembelajaran AND is checked for Sumatif or Praktikum
            if (!tp) return;
            if (ass.activityType === ActivityType.SUMATIF && !tp.isSummative) return;
            if (ass.activityType === ActivityType.PRAKTIKUM && !tp.isPracticum) return;
            if (!tp.isSummative && !tp.isPracticum) return;
        }
        const rawCode = tp ? tp.code : 'N/A';
        const cleanCode = rawCode.startsWith('TP') ? rawCode.replace(/^TP\s*/i, '') : rawCode;
        columns.push({
            id: ass.id,
            label: ass.activityType,
            date: ass.date,
            cleanCode,
            tpCode: `TP ${cleanCode}`,
            tpId: ass.tpId,
            description: tp?.description || '',
            isSAS: ass.activityType === ActivityType.SAS,
            isDeletable: true // Real assessments are deletable
        });
    });

    // Add missing summative and practicum TPs (as placeholders)
    if (rekapAssessmentType === 'Semua' || rekapAssessmentType === 'Sumatif per TP') {
        summativeTPs.forEach(tp => {
            const hasSummative = existingAssessments.some(ass => ass.tpId === tp.id && ass.activityType === ActivityType.SUMATIF);
            if (!hasSummative) {
                const rawCode = tp.code;
                const cleanCode = rawCode.startsWith('TP') ? rawCode.replace(/^TP\s*/i, '') : rawCode;
                columns.push({
                    id: `${rekapClass}::${tp.id}::${ActivityType.SUMATIF}`,
                    label: ActivityType.SUMATIF,
                    date: new Date().toISOString().split('T')[0],
                    cleanCode,
                    tpCode: `TP ${cleanCode}`,
                    tpId: tp.id,
                    description: tp.description,
                    isVirtual: true,
                    isSAS: false
                });
            }
        });
    }

    if (rekapAssessmentType === 'Semua' || rekapAssessmentType === 'Praktikum TP') {
        practicumTPs.forEach(tp => {
            const hasPracticum = existingAssessments.some(ass => ass.tpId === tp.id && ass.activityType === ActivityType.PRAKTIKUM);
            if (!hasPracticum) {
                const rawCode = tp.code;
                const cleanCode = rawCode.startsWith('TP') ? rawCode.replace(/^TP\s*/i, '') : rawCode;
                columns.push({
                    id: `${rekapClass}::${tp.id}::${ActivityType.PRAKTIKUM}`,
                    label: ActivityType.PRAKTIKUM,
                    date: new Date().toISOString().split('T')[0],
                    cleanCode,
                    tpCode: `TP ${cleanCode}`,
                    tpId: tp.id,
                    description: tp.description,
                    isVirtual: true,
                    isSAS: false
                });
            }
        });
    }

    // Sort columns: Group by Activity Type first, then cleanCode naturally, then date
    columns.sort((a, b) => {
        if (a.isSAS !== b.isSAS) return a.isSAS ? 1 : -1;
        
        const getPriority = (label: string) => {
            if (label === ActivityType.SUMATIF) return 1;
            if (label === ActivityType.PRAKTIKUM) return 2;
            return 3;
        };
        
        const prioA = getPriority(a.label);
        const prioB = getPriority(b.label);
        
        if (prioA !== prioB) return prioA - prioB;
        if (a.cleanCode !== b.cleanCode) return a.cleanCode.localeCompare(b.cleanCode, undefined, { numeric: true });
        return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    // Assign alphabetical suffixes (.a, .b, .c) when multiple assessments exist for same (tpId, label)
    const tpTypeGroups = new Map<string, any[]>();
    columns.forEach(col => {
        const key = `${col.tpId}::${col.label}`;
        if (!tpTypeGroups.has(key)) {
            tpTypeGroups.set(key, []);
        }
        tpTypeGroups.get(key)!.push(col);
    });

    tpTypeGroups.forEach(groupCols => {
        if (groupCols.length > 1) {
            groupCols.forEach((col, idx) => {
                const letter = String.fromCharCode(97 + idx); // 'a', 'b', 'c'...
                col.tpCode = `TP ${col.cleanCode}.${letter}`;
            });
        }
    });

    const rows = classStudents.map(student => {
      const studentScores: Record<string, number | null> = {};
      
      // Group scores by TP to calculate weighted average per TP
      const tpScores: Record<string, { sumatif: number[], praktikum: number[], other: number[] }> = {};

      columns.forEach(col => {
        const localScore = recapValues[col.id]?.[student.id];
        let score: number | null = null;
        
        if (localScore !== undefined) {
          score = localScore;
        } else {
          const ass = existingAssessments.find(g => g.id === col.id);
          const s = ass?.scores[student.id];
          score = s !== undefined ? s : null;
        }
        
        // Debugging injection
        if (score === null && Number(recapValues[col.id]?.[student.id]) > 0) {
            console.log("Found missing score via recapValues directly!", recapValues[col.id][student.id]);
        }
        
        studentScores[col.id] = score;

        if (score !== null) {
          if (!tpScores[col.tpId]) {
            tpScores[col.tpId] = { sumatif: [], praktikum: [], other: [] };
          }
          
          if (col.label === ActivityType.SUMATIF) {
            tpScores[col.tpId].sumatif.push(score);
          } else if (col.label === ActivityType.PRAKTIKUM) {
            tpScores[col.tpId].praktikum.push(score);
          } else {
            tpScores[col.tpId].other.push(score);
          }
        }
      });

      // Calculate weighted average
      let totalTpScore = 0;
      let tpCount = 0;
      let sasScore = 0;
      let hasSas = false;
      
      let sumSumatif = 0;
      let countSumatif = 0;
      let sumPraktikum = 0;
      let countPraktikum = 0;

      Object.entries(tpScores).forEach(([tpId, data]) => {
        const avgSumatif = data.sumatif.length > 0 ? data.sumatif.reduce((a, b) => a + b, 0) / data.sumatif.length : null;
        const avgPraktikum = data.praktikum.length > 0 ? data.praktikum.reduce((a, b) => a + b, 0) / data.praktikum.length : null;
        const avgOther = data.other.length > 0 ? data.other.reduce((a, b) => a + b, 0) / data.other.length : null;

        if (avgSumatif !== null) { sumSumatif += avgSumatif; countSumatif++; }
        if (avgPraktikum !== null) { sumPraktikum += avgPraktikum; countPraktikum++; }

        let tpFinal = 0;
        if (avgSumatif !== null && avgPraktikum !== null) {
          tpFinal = (avgSumatif * sumatifWeight + avgPraktikum * praktikumWeight) / 100;
        } else if (avgSumatif !== null) {
          tpFinal = avgSumatif;
        } else if (avgPraktikum !== null) {
          tpFinal = avgPraktikum;
        } else if (avgOther !== null) {
          tpFinal = avgOther;
        } else {
          return; // Skip if no data
        }

        // Check if this "TP" is actually SAS
        const isSasGroup = columns.some(c => c.tpId === tpId && c.isSAS);
        if (isSasGroup) {
          sasScore = tpFinal;
          hasSas = true;
        } else {
          totalTpScore += tpFinal;
          tpCount++;
        }
      });

      let finalAverage = 0;
      const avgTp = tpCount > 0 ? totalTpScore / tpCount : 0;
      
      if (hasSas) {
        finalAverage = Math.round((avgTp + sasScore) / 2);
      } else {
        finalAverage = Math.round(avgTp);
      }

      // Calculate Total Score (Sum of all numerical scores provided in the columns)
      let totalValueSum = 0;
      Object.values(studentScores).forEach(val => {
        if (typeof val === 'number') {
          totalValueSum += val;
        }
      });

      // Calculate Alpa Count for this student across teacher's agendas
      const teacherAgendas = agendas.filter(a => 
        (a.teacherId === activeUser.id || a.teacherId === activeUser.username) &&
        (a.subject?.trim().toLowerCase() === activeUser.subject?.trim().toLowerCase())
      ).map(a => a.id);
      
      const alpaCount = attendanceRecords.filter(r => 
        r.studentId === student.id && 
        (teacherAgendas.includes(r.agendaId) || r.teacherId === activeUser.id || r.teacherId === activeUser.username) && 
        r.status?.trim().toLowerCase() === AttendanceStatus.Alpa.toLowerCase()
      ).length;

      return {
        studentId: student.id,
        studentName: student.name,
        gender: student.gender,
        scores: studentScores,
        average: finalAverage,
        total: totalValueSum,
        avgSumatif: countSumatif > 0 ? Math.round(sumSumatif / countSumatif) : 0,
        avgPraktikum: countPraktikum > 0 ? Math.round(sumPraktikum / countPraktikum) : 0,
        sasScore: sasScore,
        alpaCount
      };
    });

    // 4. Sort rows
    const sortedRows = [...rows].sort((a, b) => {
      const { key, direction } = rekapSortConfig;
      let valA = a[key as keyof typeof a];
      let valB = b[key as keyof typeof b];

      // Always fallback to studentName for consistency
      if (valA === valB && key !== 'studentName') {
        return a.studentName.localeCompare(b.studentName);
      }

      if (typeof valA === 'string' && typeof valB === 'string') {
        const cmp = valA.localeCompare(valB);
        return direction === 'asc' ? cmp : -cmp;
      }

      const numA = Number(valA) || 0;
      const numB = Number(valB) || 0;
      return direction === 'asc' ? numA - numB : numB - numA;
    });

    return { columns, rows: sortedRows };
  }, [rekapClass, students, assessmentGrades, objectives, recapValues, sumatifWeight, praktikumWeight, agendas, attendanceRecords, activeUser.id, rekapSortConfig]);

  const handleRekapSort = (key: 'studentName' | 'average' | 'total') => {
    setRekapSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleSaveWeights = async (isAuto = false) => {
    if (!onUpdateTeacher) return;
    setIsSavingWeights(true);
    try {
      const updatedTeacher = {
        ...activeUser,
        sumatifWeight,
        praktikumWeight,
        weightDaily,
        weightSas
      };
      await onUpdateTeacher(updatedTeacher);
    } catch (error) {
      console.error("Failed to save weights:", error);
    } finally {
      setIsSavingWeights(false);
    }
  };

  // Auto-save effect for weights
  useEffect(() => {
    if (activeUser && (
      activeUser.sumatifWeight !== sumatifWeight ||
      activeUser.praktikumWeight !== praktikumWeight ||
      activeUser.weightDaily !== weightDaily ||
      activeUser.weightSas !== weightSas
    )) {
      const timer = setTimeout(() => {
        handleSaveWeights(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [sumatifWeight, praktikumWeight, weightDaily, weightSas, activeUser]);

  const addLogoToWorksheet = async (workbook: ExcelJS.Workbook, worksheet: ExcelJS.Worksheet) => {
    const addImage = async (url: string, col: number, row: number) => {
      try {
        let base64Data = '';
        let extension = 'png';
        let imgWidth = 80;
        let imgHeight = 80;
        
        if (url.startsWith('data:image')) {
          base64Data = url.split(',')[1];
          const mime = url.substring(5, url.indexOf(';'));
          extension = mime.split('/')[1] || 'png';
          
          await new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              imgWidth = img.width;
              imgHeight = img.height;
              resolve();
            };
            img.onerror = () => resolve();
            img.src = url;
          });
        } else {
          const response = await fetch(url);
          const blob = await response.blob();
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.readAsDataURL(blob);
          });
          extension = blob.type.split('/')[1] || 'png';
          
          await new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              imgWidth = img.width;
              imgHeight = img.height;
              URL.revokeObjectURL(img.src);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(img.src);
              resolve();
            };
            img.src = URL.createObjectURL(blob);
          });
        }

        const logoId = workbook.addImage({
          base64: base64Data,
          extension: extension as any,
        });
        
        const targetHeight = 70;
        const targetWidth = (imgWidth / imgHeight) * targetHeight;
        
        worksheet.addImage(logoId, {
          tl: { col: col, row: row },
          ext: { width: targetWidth, height: targetHeight }
        });
      } catch (error) {
        console.error('Failed to add logo to excel:', error);
      }
    };

    if (schoolSettings.departmentLogoUrl) {
      await addImage(schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      await addImage(schoolSettings.logoUrl, 11.1, 0.2);
    }
  };

  const studentSummaryData = useMemo(() => {
    if (!selectedStudentForSummary) return null;
    
    // Get teacher's TPs only
    const teacherTpIds = objectives.map(o => o.id);

    // Nilai Sumatif (Sumatif per TP)
    const summativeGrades = assessmentGrades
      .filter(g => g.class === selectedStudentForSummary.class && g.activityType === ActivityType.SUMATIF && teacherTpIds.includes(g.tpId))
      .map(g => ({
        tpId: g.tpId,
        score: g.scores[selectedStudentForSummary.id] || 0,
        date: g.date
      })).filter(g => g.score > 0);

    // Nilai Praktikum
    const praktikumGrades = assessmentGrades
      .filter(g => g.class === selectedStudentForSummary.class && g.activityType === ActivityType.PRAKTIKUM && teacherTpIds.includes(g.tpId))
      .map(g => ({
        tpId: g.tpId,
        score: g.scores[selectedStudentForSummary.id] || 0,
        date: g.date
      })).filter(g => g.score > 0);

    // SAS Grades
    const studentSas = sasGrades.filter(g => g.studentId === selectedStudentForSummary.id && g.subject === activeUser.subject);
    const latestSas = studentSas.length > 0 ? studentSas[studentSas.length - 1] : null;

    // Attendance stats - count all for this student in this teacher's class sessions
    const teacherAgendas = agendas.filter(a => 
      (a.teacherId === activeUser.id || a.teacherId === activeUser.username) &&
      (a.subject?.trim().toLowerCase() === activeUser.subject?.trim().toLowerCase())
    ).map(a => a.id);
    
    const studentAttendance = attendanceRecords.filter(r => 
      r.studentId === selectedStudentForSummary.id && 
      (teacherAgendas.includes(r.agendaId) || r.teacherId === activeUser.id || r.teacherId === activeUser.username)
    );
    
    const hadirCount = studentAttendance.filter(r => {
      const s = r.status?.trim().toLowerCase();
      return s === AttendanceStatus.Hadir.toLowerCase() || s === AttendanceStatus.Terlambat.toLowerCase() || s === AttendanceStatus.Pulang.toLowerCase();
    }).length;
    const alpaCount = studentAttendance.filter(r => r.status?.trim().toLowerCase() === AttendanceStatus.Alpa.toLowerCase()).length;
    const totalAkses = studentAttendance.length;

    return { 
        summative: summativeGrades, 
        praktikum: praktikumGrades, 
        sas: latestSas,
        attendance: {
            hadir: hadirCount,
            alpa: alpaCount,
            total: totalAkses
        }
    };
  }, [selectedStudentForSummary, assessmentGrades, sasGrades, attendanceRecords, objectives, agendas, activeUser.id, activeUser.subject]);

  const handleExportRecap = async () => {
    if (recapData.rows.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rekap Nilai');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Add Logo
    await addLogoToWorksheet(workbook, worksheet);

    // Add Header (Kop)
    worksheet.mergeCells('B1:K1');
    worksheet.getCell('B1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('B1').font = { bold: true, size: 12 };
    worksheet.getCell('B1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B2:K2');
    worksheet.getCell('B2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('B2').font = { bold: true, size: 12 };
    worksheet.getCell('B2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B3:K3');
    worksheet.getCell('B3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('B3').font = { bold: true, size: 16 };
    worksheet.getCell('B3').alignment = { horizontal: 'center' };

    const address = schoolSettings.address || '';
    const addressParts = address.split('\n');
    
    if (addressParts.length >= 2) {
      worksheet.mergeCells('B4:K4');
      worksheet.getCell('B4').value = addressParts[0];
      worksheet.getCell('B4').font = { italic: true, size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };

      worksheet.mergeCells('B5:K5');
      worksheet.getCell('B5').value = addressParts[1];
      worksheet.getCell('B5').font = { italic: true, size: 10 };
      worksheet.getCell('B5').alignment = { horizontal: 'center' };
      
      worksheet.mergeCells('A6:L6');
      worksheet.getCell('A6').border = { bottom: { style: 'thick' } };
    } else {
      worksheet.mergeCells('B4:K4');
      worksheet.getCell('B4').value = address;
      worksheet.getCell('B4').font = { italic: true, size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };

      worksheet.mergeCells('A5:L5');
      worksheet.getCell('A5').border = { bottom: { style: 'thick' } };
    }

    worksheet.addRow([]);
    worksheet.addRow([]);
    
    worksheet.mergeCells('A6:L6');
    worksheet.getCell('A6').value = 'REKAPITULASI NILAI SUMATIF SISWA';
    worksheet.getCell('A6').font = { bold: true, size: 12 };
    worksheet.getCell('A6').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A7:L7');
    worksheet.getCell('A7').value = `TAHUN PELAJARAN ${new Date().getFullYear()}/${new Date().getFullYear() + 1}`;
    worksheet.getCell('A7').font = { bold: true };
    worksheet.getCell('A7').alignment = { horizontal: 'center' };
    
    worksheet.addRow([]);
    worksheet.addRow(['', 'Mata Pelajaran', ':', activeUser.subject]);
    worksheet.addRow(['', 'Nama Guru', ':', activeUser.name]);
    worksheet.addRow(['', 'Kelas', ':', rekapClass]);
    worksheet.addRow([]);

    // Add Table Header
    const headerRow = worksheet.addRow([
      'NO',
      'NAMA SISWA',
      ...recapData.columns.map(col => col.label.toUpperCase()),
      'JUMLAH',
      'RATA-RATA'
    ]);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Add Table Data
    recapData.rows.forEach((row, idx) => {
        const dataRow = worksheet.addRow([
            idx + 1,
            row.studentName.toUpperCase(),
            ...recapData.columns.map(col => row.scores[col.id] ?? '-'),
            row.total,
            row.average
        ]);
        dataRow.eachCell((cell) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
    });

    // Add Signature
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', '', '', '', `Kawali, ${today}`]);
    worksheet.addRow(['', '', '', '', '', '', '', '', 'Guru Mata Pelajaran,']);
    worksheet.addRow(['', '', '', '', '', '', '', '', '']);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', '', '', '', activeUser.name]);
    worksheet.addRow(['', '', '', '', '', '', '', '', '']);

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
        FileSaver.saveAs(blob, `Rekap_Nilai_${rekapClass}_${activeUser.subject}.xlsx`);
    });
  };

  const handleExportFormatif = async () => {
    if (!selectedClass || !selectedTP || filteredStudentsForFormative.length === 0) return;

    const tp = objectives.find(o => o.id === selectedTP);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Formatif TP');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Add Logo
    const addImageToWorksheet = async (url: string, range: string) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const imageId = workbook.addImage({
          buffer: arrayBuffer,
          extension: 'png',
        });
        worksheet.addImage(imageId, range);
      } catch (e) {
        console.error("Failed to add image", e);
      }
    };

    if (schoolSettings.departmentLogoUrl) {
      await addImageToWorksheet(schoolSettings.departmentLogoUrl, 'A1:B3');
    }
    if (schoolSettings.logoUrl) {
      await addImageToWorksheet(schoolSettings.logoUrl, 'G1:H3');
    }

    // Add Header (Kop)
    worksheet.mergeCells('C1:F1');
    worksheet.getCell('C1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('C1').font = { bold: true, size: 12 };
    worksheet.getCell('C1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('C2:F2');
    worksheet.getCell('C2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('C2').font = { bold: true, size: 12 };
    worksheet.getCell('C2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('C3:F3');
    worksheet.getCell('C3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('C3').font = { bold: true, size: 14 };
    worksheet.getCell('C3').alignment = { horizontal: 'center' };

    worksheet.addRow([]);
    worksheet.mergeCells('A5:F5');
    worksheet.getCell('A5').value = 'REKAPITULASI CAPAIAN KOMPETENSI (FORMATIF TP)';
    worksheet.getCell('A5').font = { bold: true, size: 12 };
    worksheet.getCell('A5').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A6:F6');
    worksheet.getCell('A6').value = `KELAS: ${selectedClass}`;
    worksheet.getCell('A6').font = { bold: true };
    worksheet.getCell('A6').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A7:F7');
    worksheet.getCell('A7').value = `TP: ${tp?.code || '-'} - ${tp?.description || '-'}`;
    worksheet.getCell('A7').font = { bold: true };
    worksheet.getCell('A7').alignment = { horizontal: 'center' };
    worksheet.addRow([]);

    // Add Table Header
    const headerRow = worksheet.addRow(['NO', 'NAMA SISWA', 'NISN', 'CAPAIAN KOMPETENSI']);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Add Table Data
    filteredStudentsForFormative.forEach((student, idx) => {
        const dataRow = worksheet.addRow([
            idx + 1,
            student.name.toUpperCase(),
            student.nisn || student.nis || '',
            formativeValues[student.id] || 'Layak'
        ]);
        dataRow.eachCell((cell) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
    });

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
        FileSaver.saveAs(blob, `Rekap_Formatif_${selectedClass}_${tp?.code || 'TP'}.xlsx`);
    });
  };


  const handleExportSas = async () => {
    if (!sasClass || sortedSasStudents.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sumatif Akhir');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Add Logo
    await addLogoToWorksheet(workbook, worksheet);

    // Add Header (Kop)
    worksheet.mergeCells('B1:I1');
    worksheet.getCell('B1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('B1').font = { bold: true, size: 12 };
    worksheet.getCell('B1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B2:I2');
    worksheet.getCell('B2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('B2').font = { bold: true, size: 12 };
    worksheet.getCell('B2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('B3:I3');
    worksheet.getCell('B3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('B3').font = { bold: true, size: 16 };
    worksheet.getCell('B3').alignment = { horizontal: 'center' };

    const address = schoolSettings.address || '';
    const addressParts = address.split('\n');
    
    if (addressParts.length >= 2) {
      worksheet.mergeCells('B4:I4');
      worksheet.getCell('B4').value = addressParts[0];
      worksheet.getCell('B4').font = { size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };
      
      worksheet.mergeCells('B5:I5');
      worksheet.getCell('B5').value = addressParts[1] + (schoolSettings.postalCode ? ` Kode Pos ${schoolSettings.postalCode}` : '');
      worksheet.getCell('B5').font = { size: 10 };
      worksheet.getCell('B5').alignment = { horizontal: 'center' };
    } else {
      worksheet.mergeCells('B4:I4');
      worksheet.getCell('B4').value = address + (schoolSettings.postalCode ? ` Kode Pos ${schoolSettings.postalCode}` : '');
      worksheet.getCell('B4').font = { size: 10 };
      worksheet.getCell('B4').alignment = { horizontal: 'center' };
    }

    // Add thick border line below kop
    worksheet.getCell('A6').border = { bottom: { style: 'double' } };
    worksheet.getCell('B6').border = { bottom: { style: 'double' } };
    worksheet.getCell('C6').border = { bottom: { style: 'double' } };
    worksheet.getCell('D6').border = { bottom: { style: 'double' } };
    worksheet.getCell('E6').border = { bottom: { style: 'double' } };
    worksheet.getCell('F6').border = { bottom: { style: 'double' } };
    worksheet.getCell('G6').border = { bottom: { style: 'double' } };
    worksheet.getCell('H6').border = { bottom: { style: 'double' } };
    worksheet.getCell('I6').border = { bottom: { style: 'double' } };

    worksheet.mergeCells('A8:I8');
    worksheet.getCell('A8').value = 'DAFTAR NILAI SUMATIF AKHIR SEMESTER';
    worksheet.getCell('A8').font = { bold: true, size: 14 };
    worksheet.getCell('A8').alignment = { horizontal: 'center' };

    worksheet.addRow([]);
    worksheet.addRow(['', 'Mata Pelajaran', ':', activeUser.subject]);
    worksheet.addRow(['', 'Nama Guru', ':', activeUser.name]);
    worksheet.addRow(['', 'Kelas', ':', sasClass]);
    worksheet.addRow([]);

    const headerRow = worksheet.addRow(['NO', 'NAMA SISWA', 'NISN', 'PRESENSI (%)', 'ALPA', 'NILAI TH', 'NILAI CATATAN', 'NILAI SUMATIF AKHIR', 'NILAI SUMATIF AKHIR (EVALUASI)']);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Add Table Data
    sortedSasStudents.forEach((student, idx) => {
        const dataRow = worksheet.addRow([
            idx + 1,
            student.name,
            student.nisn || student.nis || '',
            student.percentage,
            student.alpa,
            student.nilaiTh,
            student.nilaiCatatan,
            student.score,
            student.nilaiEvaluasi
        ]);
        dataRow.eachCell((cell, colNumber) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            // Center align columns except name
            if (colNumber !== 2) {
                cell.alignment = { horizontal: 'center' };
            }
        });
    });

    const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', `Kawali, ${dateStr}`]);
    worksheet.addRow(['', '', '', '', '', 'Guru Mata Pelajaran,']);
    worksheet.addRow(['', '', '', '', '', '']);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', activeUser.name]);
    worksheet.addRow(['', '', '', '', '', '']);

    // Set Column Widths
    worksheet.getColumn(1).width = 5;  // NO
    worksheet.getColumn(2).width = 35; // NAMA
    worksheet.getColumn(3).width = 15; // NISN
    worksheet.getColumn(4).width = 15; // PRESENSI
    worksheet.getColumn(5).width = 10; // ALPA
    worksheet.getColumn(6).width = 15; // NILAI TH
    worksheet.getColumn(7).width = 15; // NILAI CATATAN
    worksheet.getColumn(8).width = 20; // NILAI SUMATIF AKHIR
    worksheet.getColumn(9).width = 30; // NILAI SUMATIF AKHIR (EVALUASI)

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
        FileSaver.saveAs(blob, `SUMATIF_AKHIR_${activeUser.subject}_KELAS_${sasClass}_${new Date().toISOString().split('T')[0]}.xlsx`);
    });
  };

  const fileInputRefSas = useRef<HTMLInputElement>(null);

  const handleImportSas = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      if (!worksheet) return;

      const newSasScores = { ...sasScores };
      
      let headerRowNumber = -1;
      
      worksheet.eachRow((row, rowNumber) => {
        const firstCell = String(row.getCell(1).value || '').toUpperCase();
        if (firstCell === 'NO' || firstCell === 'NO.') {
            headerRowNumber = rowNumber;
        } else if (headerRowNumber > 0 && rowNumber > headerRowNumber) {
            const nis = String(row.getCell(3).value || '').trim();
            const scoreValue = row.getCell(6).value;
            let score = 0;
            if (typeof scoreValue === 'number') {
                score = scoreValue;
            } else if (typeof scoreValue === 'string') {
                score = parseFloat(scoreValue) || 0;
            } else if (typeof scoreValue === 'object' && scoreValue !== null && 'result' in scoreValue) {
                // handle formula result
                score = parseFloat(String(scoreValue.result)) || 0;
            }

            if (nis && !isNaN(score)) {
                // Find student by NIS
                const matchedStudent = sortedSasStudents.find(s => s.nis === nis);
                if (matchedStudent) {
                    newSasScores[matchedStudent.id] = Math.min(100, Math.max(0, score));
                }
            }
        }
      });
      
      setSasScores(newSasScores);
      alert("Nilai Sumatif Akhir berhasil diimport! Silakan klik 'SIMPAN NILAI' untuk menyimpan ke database.");
      
    } catch (error) {
      console.error(error);
      alert("Terjadi kesalahan saat mengimpor data.");
    } finally {
        if (fileInputRefSas.current) {
            fileInputRefSas.current.value = '';
        }
    }
  };

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
        case ActivityType.KBM_DAN_QUIZ: return <PenTool className="text-cyan-500" size={20} />;
        case ActivityType.SUMATIF: return <ClipboardCheck className="text-indigo-500" size={20} />;
        case ActivityType.SAS: return <GraduationCap className="text-rose-500" size={20} />;
        default: return null;
    }
  };

  const selectClassName = "w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none font-black text-indigo-600 dark:text-indigo-400 focus:ring-4 focus:ring-indigo-500/10 transition-all text-xs";

  const getStudentNameColor = (gender: 'Laki-laki' | 'Perempuan') => {
    return gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400';
  };

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      {/* Header View Toggle */}
      <div className="flex flex-col sm:flex-row bg-white dark:bg-slate-900 p-2 rounded-[1.5rem] border border-slate-200 dark:border-slate-800 w-full sm:w-fit gap-2">
          <button 
            onClick={() => setActiveTab('rekap')}
            className={`flex items-center justify-center sm:justify-start gap-2 px-6 py-3 sm:py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'rekap' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
          >
              <BarChartHorizontal size={16} /> Penilaian Sumatif dan Praktikum TP
          </button>
          {role !== UserRole.Siswa && (
            <button 
              onClick={() => setActiveTab('kuis')}
              className={`flex items-center justify-center sm:justify-start gap-2 px-6 py-3 sm:py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'kuis' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            >
                <PenTool size={16} /> Penilaian Tugas Harian
            </button>
          )}
          <button 
            onClick={() => setActiveTab('formatif')}
            className={`flex items-center justify-center sm:justify-start gap-2 px-6 py-3 sm:py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'formatif' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
          >
              <LayoutGrid size={16} /> Penilaian Formatif (TP)
          </button>
          <button 
            onClick={() => setActiveTab('sumatif-akhir')}
            className={`flex items-center justify-center sm:justify-start gap-2 px-6 py-3 sm:py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'sumatif-akhir' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
          >
              <GraduationCap size={16} /> Penilaian SUMATIF AKHIR
          </button>
          <button 
            onClick={() => setActiveTab('rekap-buku')}
            className={`flex items-center justify-center sm:justify-start gap-2 px-6 py-3 sm:py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'rekap-buku' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
          >
              <BookMarked size={16} /> Penilaian Rekap Buku Catatan
          </button>
      </div>

      {activeTab === 'sumatif-akhir' ? (
        // VIEW SUMATIF AKHIR (REMODDED)
        <div className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500 pb-24 md:pb-10">
            <div className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex items-center gap-5">
                    <div className="w-16 h-16 bg-rose-50 dark:bg-rose-950 rounded-3xl flex items-center justify-center text-rose-500 shadow-inner">
                        <GraduationCap size={32} />
                    </div>
                    <div>
                        <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-1">
                            Sumatif Akhir
                        </h2>
                        <p className="text-sm text-slate-400 font-medium tracking-tight">Evaluasi capaian akhir semester</p>
                    </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => navigateClass(sasClass, 'prev', setSasClass)}
                            title="Kelas Sebelumnya"
                            className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-600 dark:text-slate-300 hover:text-rose-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <div className="relative group flex-1 md:flex-none">
                            <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-slate-900 text-[10px] font-black text-rose-500 tracking-widest z-10 uppercase">Pilih Rombel</label>
                            <select 
                                value={sasClass} 
                                onChange={e => setSasClass(e.target.value)}
                                className="w-full md:w-48 pl-4 pr-10 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-slate-700 dark:text-slate-200 focus:border-rose-400 focus:ring-4 focus:ring-rose-500/5 transition-all text-sm appearance-none cursor-pointer"
                            >
                                <option value="">-- Pilih Kelas --</option>
                                {sortedClasses.map(c => <option key={c} value={c}>KELAS {c}</option>)}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-rose-500 transition-colors">
                                <ChevronRight size={18} className="rotate-90" />
                            </div>
                        </div>
                        <button
                            onClick={() => navigateClass(sasClass, 'next', setSasClass)}
                            title="Kelas Berikutnya"
                            className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-600 dark:text-slate-300 hover:text-rose-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => window.print()} 
                            disabled={!sasClass}
                            className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
                            title="Cetak Data"
                        >
                            <Printer size={18} className="sm:hidden" />
                            <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Cetak</span>
                        </button>
                        <button 
                            onClick={handleExportSas}
                            disabled={!sasClass}
                            className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-all cursor-pointer disabled:opacity-50"
                            title="Download Template / Export"
                        >
                            <FileSpreadsheet size={18} className="sm:hidden" />
                            <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Export</span>
                        </button>
                        <label 
                            className={`flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-all ${!sasClass ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            title="Import dari Excel"
                        >
                            <Upload size={18} className="sm:hidden" />
                            <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Import</span>
                            <input 
                                type="file" 
                                accept=".xlsx"
                                ref={fileInputRefSas} 
                                onChange={handleImportSas} 
                                className="hidden" 
                                disabled={!sasClass}
                            />
                        </label>
                        <button 
                            onClick={() => handleSaveSas()}
                            disabled={!sasClass || isSavingSas}
                            className="flex items-center justify-center gap-3 px-6 py-3.5 bg-rose-600 text-white font-black rounded-2xl hover:bg-rose-700 shadow-xl shadow-rose-900/10 active:scale-[0.98] disabled:opacity-50 transition-all text-sm grow md:grow-0 cursor-pointer"
                        >
                            {isSavingSas ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span className="hidden sm:inline">MENYIMPAN...</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18} /> <span className="hidden sm:inline">SIMPAN NILAI</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {sasClass ? (
                <>
                    {/* Persentase Bobot Evaluasi */}
                    <div id="bobot-evaluasi-container" className="bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-3xl p-6 mb-6 flex flex-wrap items-center justify-between gap-6">
                        <div className="space-y-1">
                            <h3 className="text-sm font-black text-slate-800 dark:text-white tracking-tight uppercase">
                                Pengaturan Bobot Nilai Sumatif Akhir (Evaluasi)
                            </h3>
                            <p className="text-xs text-slate-400 font-medium leading-normal max-w-xl">
                                Atur persentase bobot antara nilai tugas harian/catatan buku dan nilai sumatif akhir. Kedua bobot akan otomatis sinkron agar total mencapai 100%.
                            </p>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
                            <div className="flex flex-col gap-1.5 relative group/input w-40">
                                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    TH & Buku (%)
                                </label>
                                <div className="relative">
                                    <input
                                        id="weight-daily-input"
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={weightDaily}
                                        onChange={(e) => handleWeightDailyChange(Number(e.target.value))}
                                        className="w-full px-4 py-3 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-2xl font-mono font-black text-slate-800 dark:text-white focus:border-rose-400 outline-none transition-all text-sm pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="20"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-xs font-black">%</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center pt-5">
                                <span className="text-lg font-black text-slate-300 dark:text-slate-700 font-mono">+</span>
                            </div>
                            <div className="flex flex-col gap-1.5 relative group/input w-44">
                                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    Sumatif Akhir (%)
                                </label>
                                <div className="relative">
                                    <input
                                        id="weight-sas-input"
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={weightSas}
                                        onChange={(e) => handleWeightSasChange(Number(e.target.value))}
                                        className="w-full px-4 py-3 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-2xl font-mono font-black text-slate-800 dark:text-white focus:border-rose-400 outline-none transition-all text-sm pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="80"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-xs font-black">%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden border border-slate-100 dark:border-slate-800 shadow-sm">
                    {/* Desktop View */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                                <tr className="text-slate-400 uppercase text-[10px] font-black tracking-[0.2em]">
                                    <th className="px-8 py-5">No</th>
                                    <th className="px-4 py-5">
                                        <button onClick={() => handleSasSort('name')} className="flex items-center gap-2 hover:text-rose-500 transition-colors">
                                            Nama Siswa
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'name' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                    <th className="px-4 py-5">
                                        <button onClick={() => handleSasSort('percentage')} className="flex items-center gap-2 hover:text-rose-500 transition-colors text-center mx-auto">
                                            Hadir
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'percentage' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                    <th className="px-4 py-5">
                                        <button onClick={() => handleSasSort('alpa')} className="flex items-center gap-2 hover:text-rose-500 transition-colors text-center mx-auto">
                                            Alpa
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'alpa' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                    <th className="px-4 py-5 text-center">
                                        <button onClick={() => handleSasSort('nilaiTh')} className="flex items-center gap-2 hover:text-rose-500 transition-colors text-center mx-auto">
                                            Nilai TH
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'nilaiTh' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                    <th className="px-4 py-5 text-center">
                                        <button onClick={() => handleSasSort('nilaiCatatan')} className="flex items-center gap-2 hover:text-rose-500 transition-colors text-center mx-auto">
                                            Nilai Catatan
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'nilaiCatatan' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                    <th className="px-8 py-5 text-right">
                                        <button onClick={() => handleSasSort('score')} className="flex items-center gap-2 hover:text-rose-500 transition-colors ml-auto">
                                            Nilai Sumatif Akhir
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'score' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                    <th className="px-8 py-5 text-right">
                                        <button onClick={() => handleSasSort('nilaiEvaluasi')} className="flex items-center gap-2 hover:text-rose-500 transition-colors ml-auto">
                                            Nilai Sumatif Akhir (Evaluasi)
                                            <ArrowUpDown size={12} className={sasSortConfig.key === 'nilaiEvaluasi' ? 'text-rose-500' : 'opacity-30'} />
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                {sortedSasStudents.map((student, idx) => (
                                    <tr key={student.id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-300">
                                        <td className="px-8 py-5 font-black text-slate-300 group-hover:text-slate-400 transition-colors">{idx + 1}</td>
                                        <td className="px-4 py-5 min-w-[200px]">
                                            <p className={`font-black tracking-tight ${getStudentNameColor(student.gender)}`}>{student.name}</p>
                                            <p className="text-[10px] font-bold text-slate-400 mt-0.5">NISN: {student.nisn || student.nis}</p>
                                        </td>
                                        <td className="px-4 py-5 text-center">
                                            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-full text-[10px] font-black tracking-widest border border-emerald-100 dark:border-emerald-800/10">
                                                {student.percentage}%
                                            </div>
                                        </td>
                                        <td className="px-4 py-5 text-center">
                                            {student.alpa > 0 || student.sakit > 0 || student.izin > 0 || student.dispen > 0 ? (
                                                <div className="flex flex-col items-center gap-1">
                                                    {student.alpa > 0 && <span className="inline-flex items-center px-2 py-1 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-full text-xs font-black tracking-tight border-2 border-rose-200 dark:border-rose-800/40 shadow-sm animate-pulse">A: {student.alpa}</span>}
                                                    {student.sakit > 0 && <span className="inline-flex items-center px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-full text-[9px] font-black tracking-tight border border-amber-100 dark:border-amber-800/10">S: {student.sakit}</span>}
                                                    {student.izin > 0 && <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-[9px] font-black tracking-tight border border-indigo-100 dark:border-indigo-800/10">I: {student.izin}</span>}
                                                    {student.dispen > 0 && <span className="inline-flex items-center px-2 py-0.5 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 rounded-full text-[9px] font-black tracking-tight border border-cyan-100 dark:border-cyan-800/10">D: {student.dispen}</span>}
                                                </div>
                                            ) : (
                                                <span className="text-[10px] font-black text-slate-300">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-5 text-center">
                                            <span className="inline-block px-3 py-1.5 rounded-xl bg-cyan-50 dark:bg-cyan-950/30 text-cyan-600 dark:text-cyan-400 font-mono font-black text-sm">
                                                {student.nilaiTh}
                                            </span>
                                        </td>
                                        <td className="px-4 py-5 text-center">
                                            <span className="inline-block px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 font-mono font-black text-sm">
                                                {student.nilaiCatatan}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex justify-end">
                                                <div className="relative w-28 group/input">
                                                    <input 
                                                        type="number" step="any"
                                                        min="0"
                                                        max="100"
                                                        value={sasScores[student.id] ?? ''}
                                                        onChange={(e) => handleSasScoreChange(student.id, e.target.value)}
                                                        className="no-spinners w-full px-4 py-2.5 bg-slate-50/50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-center font-black text-slate-900 dark:text-white outline-none focus:ring-4 focus:ring-rose-500/10 focus:border-rose-400 transition-all text-sm"
                                                        placeholder="0"
                                                    />
                                                    <div className="absolute top-0 right-0 p-1 opacity-0 group-hover/input:opacity-100 transition-opacity">
                                                        <div className="w-1.5 h-1.5 bg-rose-500 rounded-full"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex justify-end">
                                                <span className="inline-block px-4 py-2 bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 rounded-2xl text-center font-black text-purple-900 dark:text-purple-200 text-sm">
                                                    {student.nilaiEvaluasi}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View */}
                    <div className="block sm:hidden divide-y divide-slate-50 dark:divide-slate-800">
                        {/* Mobile Sorting Controls */}
                        <div className="p-4 bg-slate-50/50 dark:bg-slate-800/50 grid grid-cols-5 gap-1">
                             <button onClick={() => handleSasSort('name')} className={`flex items-center justify-center gap-1 p-1 rounded-xl text-[8px] font-black uppercase tracking-wider border transition-all ${sasSortConfig.key === 'name' ? 'bg-rose-500 border-rose-500 text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400'}`}>
                                Nama <ArrowUpDown size={6} />
                             </button>
                             <button onClick={() => handleSasSort('nilaiTh')} className={`flex items-center justify-center gap-1 p-1 rounded-xl text-[8px] font-black uppercase tracking-wider border transition-all ${sasSortConfig.key === 'nilaiTh' ? 'bg-rose-500 border-rose-500 text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400'}`}>
                                TH <ArrowUpDown size={6} />
                             </button>
                             <button onClick={() => handleSasSort('nilaiCatatan')} className={`flex items-center justify-center gap-1 p-1 rounded-xl text-[8px] font-black uppercase tracking-wider border transition-all ${sasSortConfig.key === 'nilaiCatatan' ? 'bg-rose-500 border-rose-500 text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400'}`}>
                                Buku <ArrowUpDown size={6} />
                             </button>
                             <button onClick={() => handleSasSort('score')} className={`flex items-center justify-center gap-1 p-1 rounded-xl text-[8px] font-black uppercase tracking-wider border transition-all ${sasSortConfig.key === 'score' ? 'bg-rose-500 border-rose-500 text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400'}`}>
                                Nilai <ArrowUpDown size={6} />
                             </button>
                             <button onClick={() => handleSasSort('nilaiEvaluasi')} className={`flex items-center justify-center gap-1 p-1 rounded-xl text-[8px] font-black uppercase tracking-wider border transition-all ${sasSortConfig.key === 'nilaiEvaluasi' ? 'bg-rose-500 border-rose-500 text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400'}`}>
                                Eval <ArrowUpDown size={6} />
                             </button>
                        </div>
                        {sortedSasStudents.map((student, idx) => (
                            <div key={student.id} className="p-6 flex flex-col gap-4 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="flex items-center justify-center w-6 h-6 rounded-xl bg-slate-100 dark:bg-slate-800 text-[10px] font-black text-slate-400">{idx + 1}</span>
                                            <span className="text-[10px] font-bold text-slate-400 tracking-wider line-clamp-1">{student.nis} {student.nisn ? `| ${student.nisn}` : ''}</span>
                                        </div>
                                        <h4 className={`font-black text-sm lg:text-base tracking-tight mb-2 ${getStudentNameColor(student.gender)}`}>{student.name}</h4>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                Hadir: {student.percentage}%
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-cyan-50 dark:bg-cyan-950 text-cyan-600 dark:text-cyan-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                TH: {student.nilaiTh}
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                Buku: {student.nilaiCatatan}
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                Eval: {student.nilaiEvaluasi}
                                            </div>
                                            {student.alpa > 0 && (
                                                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-white rounded-lg text-xs font-black uppercase tracking-tight shadow-sm">
                                                    ALPA: {student.alpa}
                                                </div>
                                            )}
                                            {student.sakit > 0 && (
                                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                    S: {student.sakit}
                                                </div>
                                            )}
                                            {student.izin > 0 && (
                                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                    I: {student.izin}
                                                </div>
                                            )}
                                            {student.dispen > 0 && (
                                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-cyan-50 dark:bg-cyan-950 text-cyan-600 dark:text-cyan-400 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                    D: {student.dispen}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2 shrink-0">
                                        <label className="text-[9px] font-black text-rose-500 uppercase tracking-[0.2em] leading-none">N. Sumatif Akhir</label>
                                        <input 
                                            type="number" step="any"
                                            min="0"
                                            max="100"
                                            value={sasScores[student.id] ?? ''}
                                            onChange={(e) => handleSasScoreChange(student.id, e.target.value)}
                                            className="no-spinners w-20 px-4 py-3 bg-slate-50/50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-center font-black text-slate-900 dark:text-white outline-none focus:ring-4 focus:ring-rose-500/10 focus:border-rose-400 transition-all text-sm"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
              </>
            ) : (
                <div className="py-24 text-center bg-slate-50/50 dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-[3rem]">
                    <div className="w-24 h-24 bg-white dark:bg-slate-900 rounded-[2.5rem] flex items-center justify-center text-slate-200 mx-auto mb-6 shadow-sm">
                        <GraduationCap size={48} />
                    </div>
                    <h3 className="text-xl font-black text-slate-400 tracking-tight mb-2">Pilih Kelas Terlebih Dahulu</h3>
                    <p className="text-sm text-slate-400 font-medium max-w-xs mx-auto">Silahkan pilih kelas dari dropdown di atas untuk mulai menginput nilai Sumatif Akhir.</p>
                </div>
            )}
        </div>
      ) : activeTab === 'kuis' ? (
        // VIEW PENILAIAN KUIS
        <div className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500 pb-24 md:pb-10 space-y-8">
          {/* Header Title & Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-cyan-50 dark:bg-cyan-950/60 rounded-3xl flex items-center justify-center text-cyan-600 dark:text-cyan-400 shadow-inner border border-cyan-100 dark:border-cyan-900/40">
                <PenTool size={32} />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-1">
                  Penilaian Tugas Harian (KBM dan Asesmen Harian)
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 font-medium tracking-tight">
                  Pencatatan nilai tugas harian berdasarkan agenda mengajar jenis kegiatan "KBM dan Asesmen Harian".
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => navigateClass(quizClass, 'prev', setQuizClass)}
                  title="Kelas Sebelumnya"
                  className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-cyan-50 dark:hover:bg-cyan-950/40 text-slate-600 dark:text-slate-300 hover:text-cyan-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="relative group flex-1 md:flex-none">
                  <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-slate-900 text-[10px] font-black text-cyan-600 dark:text-cyan-400 tracking-widest z-10 uppercase">
                    Pilih Rombel
                  </label>
                  <select 
                    value={quizClass} 
                    onChange={e => setQuizClass(e.target.value)}
                    className="w-full md:w-56 pl-4 pr-10 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-slate-700 dark:text-slate-200 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/5 transition-all text-xs appearance-none cursor-pointer"
                  >
                    <option value="">-- Pilih Kelas --</option>
                    {sortedClasses.map(c => {
                      const count = quizAgendaCountMap[normalizeClass(c)] || 0;
                      return (
                        <option key={c} value={c}>
                          KELAS {c} {count > 0 ? `(${count} Asesmen)` : '(0 Asesmen)'}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-cyan-500 transition-colors">
                    <ChevronRight size={18} className="rotate-90" />
                  </div>
                </div>
                <button
                  onClick={() => navigateClass(quizClass, 'next', setQuizClass)}
                  title="Kelas Berikutnya"
                  className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-cyan-50 dark:hover:bg-cyan-950/40 text-slate-600 dark:text-slate-300 hover:text-cyan-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              {selectedQuizAgenda && (
                <div className="flex items-center gap-2 px-4 py-3 bg-cyan-50 dark:bg-cyan-950/40 border border-cyan-200 dark:border-cyan-800/60 text-cyan-700 dark:text-cyan-300 rounded-2xl text-xs font-bold shadow-xs">
                  {isSavingQuiz ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Menyimpan ke Firebase...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} className="text-emerald-500" />
                      <span>Tersimpan Otomatis ke Firebase</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* SECTION: KARTU-KARTU KECIL TUGAS HARIAN BELUM DINILAI OLEH GURU */}
          {ungradedQuizAgendas.length > 0 && (
            <div className="p-4 sm:p-5 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-slate-900/5 dark:from-amber-950/30 dark:via-slate-800/40 dark:to-slate-900/40 rounded-3xl border-2 border-amber-300/60 dark:border-amber-700/50 space-y-3 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-amber-500 text-white rounded-2xl shadow-sm shadow-amber-500/20">
                    <Clock size={18} />
                  </div>
                  <div>
                    <h3 className="text-xs sm:text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                      Tugas Harian Belum Dinilai oleh Guru
                      <span className="px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black shadow-xs">
                        {ungradedQuizAgendas.length} Tugas
                      </span>
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                      Pilih kartu kecil di bawah untuk langsung menuju rombel & mengisi nilai tugas siswa.
                    </p>
                  </div>
                </div>
              </div>

              {/* Grid Kartu-kartu Kecil */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 pt-1">
                {ungradedQuizAgendas.map(({ agenda, gradedCount, presentStudentCount, absentStudentCount, ungradedCount }) => {
                  const isCurrentSelected = quizClass === agenda.class && selectedQuizAgendaId === agenda.id;
                  return (
                    <button
                      key={`ungraded_card_${agenda.id}`}
                      type="button"
                      onClick={() => {
                        setQuizClass(agenda.class);
                        setSelectedQuizAgendaId(agenda.id);
                      }}
                      className={`p-2.5 sm:p-3 text-left rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between gap-1.5 min-h-[85px] relative group ${
                        isCurrentSelected
                          ? 'bg-amber-100 dark:bg-amber-950/80 border-amber-500 shadow-md shadow-amber-500/20 ring-2 ring-amber-500/30 scale-[1.02]'
                          : 'bg-white dark:bg-slate-800/90 border-amber-200 dark:border-amber-900/40 hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1 w-full">
                        <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border border-cyan-500/20">
                          {agenda.class}
                        </span>
                        <span className="text-[8px] font-bold text-slate-400">
                          {new Date(agenda.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                        </span>
                      </div>

                      <div className="text-[10px] font-extrabold text-slate-800 dark:text-slate-100 leading-snug line-clamp-2 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {agenda.penilaian || agenda.topic || 'Tugas Harian'}
                      </div>

                      <div className="flex flex-col gap-0.5 w-full pt-1 border-t border-slate-100 dark:border-slate-700/60 text-[9px]">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-amber-600 dark:text-amber-400 font-black flex items-center gap-0.5">
                            <Clock size={10} /> {ungradedCount} Belum
                          </span>
                          <span className="text-slate-500 dark:text-slate-400 font-bold">
                            {gradedCount}/{presentStudentCount}
                          </span>
                        </div>
                        {absentStudentCount > 0 && (
                          <div className="text-[8px] font-bold text-rose-500 dark:text-rose-400 flex items-center gap-0.5">
                            🚫 {absentStudentCount} Tdk Hadir (Tanpa Nilai)
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stats Cards for Tugas Harian per Kelas */}
          {quizClass && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-5 bg-gradient-to-br from-indigo-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-indigo-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Total Siswa</span>
                <div className="text-2xl font-black text-slate-800 dark:text-white font-mono">
                  {students.filter(s => s.class === quizClass && s.status === 'Aktif').length} <span className="text-xs font-bold text-slate-400">Siswa</span>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-br from-cyan-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-cyan-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-cyan-600 dark:text-cyan-400 uppercase tracking-widest block">Jumlah Tugas Harian</span>
                <div className="text-2xl font-black text-cyan-600 dark:text-cyan-400 font-mono">
                  {quizAgendasForClass.length} <span className="text-xs font-bold text-slate-400">Tugas / Agenda</span>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-br from-emerald-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-emerald-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest block">Rata-rata Nilai Tugas Kelas</span>
                <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                  {(() => {
                    const classGrades = assessmentGrades.filter(g => 
                      normalizeClass(g.class) === normalizeClass(quizClass) && 
                      ((g.activityType as any) === ActivityType.KBM_DAN_QUIZ || (g.activityType as any) === 'Pembelajaran dan Asesmen' || (g.activityType as any) === 'KBM dan Asesmen Harian')
                    );
                    let sum = 0;
                    let count = 0;
                    classGrades.forEach(g => {
                      Object.values(g.scores || {}).forEach(score => {
                        const num = Number(score);
                        if (!isNaN(num) && score !== undefined && score !== null && String(score) !== '') {
                          sum += num;
                          count++;
                        }
                      });
                    });
                    return count > 0 ? (sum / count).toFixed(1) : '-';
                  })()} <span className="text-xs font-bold text-slate-400">Poin</span>
                </div>
              </div>
            </div>
          )}

          {/* CHECK IF THERE ARE AGENDAS WITH "KBM DAN ASESMEN HARIAN" */}
          {quizAgendasForClass.length > 0 ? (
            <div className="space-y-6">
              {/* Agenda Quiz Selector Cards */}
              <div className="p-5 bg-gradient-to-br from-cyan-50/60 to-slate-50 dark:from-cyan-950/20 dark:to-slate-800/20 rounded-3xl border border-cyan-100 dark:border-cyan-900/40 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="text-cyan-600 dark:text-cyan-400" size={18} />
                    <span className="text-xs font-black text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                      Daftar Agenda "KBM dan Asesmen Harian" - Kelas {quizClass}:
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 self-start sm:self-auto">
                    <button
                      type="button"
                      onClick={() => setQuizFilterMode('all')}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all cursor-pointer ${
                        quizFilterMode === 'all'
                          ? 'bg-cyan-600 text-white shadow-xs'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                      }`}
                    >
                      Semua ({quizAgendasForClass.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuizFilterMode('ungraded')}
                      className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer ${
                        quizFilterMode === 'ungraded'
                          ? 'bg-amber-500 text-white shadow-xs'
                          : 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40 hover:bg-amber-100'
                      }`}
                    >
                      <Clock size={12} /> Belum Dinilai ({quizAgendasForClass.filter(a => {
                        const classNorm = normalizeClass(a.class);
                        const cStus = students.filter(s => normalizeClass(s.class) === classNorm && s.status === 'Aktif');
                        const exG = assessmentGrades.find(g => g.id === a.id);
                        const scs = exG?.scores || recapValues[a.id] || {};
                        let cnt = 0;
                        let presentCnt = 0;
                        cStus.forEach(s => {
                          const attRecord = attendanceRecords.find(r => r.studentId === s.id && r.agendaId === a.id);
                          const attStatusLower = attRecord?.status?.trim().toLowerCase();
                          const isAbsent = attStatusLower && (
                            attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Izin.toLowerCase()
                          );
                          if (!isAbsent) {
                            presentCnt++;
                            if (scs[s.id] !== undefined && scs[s.id] !== null && String(scs[s.id]).trim() !== '') cnt++;
                          }
                        });
                        return presentCnt > 0 && cnt < presentCnt;
                      }).length})
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 pt-1">
                  {quizAgendasForClass
                    .filter(agenda => {
                      if (quizFilterMode === 'ungraded') {
                        const classNorm = normalizeClass(agenda.class);
                        const cStus = students.filter(s => normalizeClass(s.class) === classNorm && s.status === 'Aktif');
                        const exG = assessmentGrades.find(g => g.id === agenda.id);
                        const scs = exG?.scores || recapValues[agenda.id] || {};
                        let cnt = 0;
                        let presentCnt = 0;
                        cStus.forEach(s => {
                          const attRecord = attendanceRecords.find(r => r.studentId === s.id && r.agendaId === agenda.id);
                          const attStatusLower = attRecord?.status?.trim().toLowerCase();
                          const isAbsent = attStatusLower && (
                            attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Izin.toLowerCase()
                          );
                          if (!isAbsent) {
                            presentCnt++;
                            if (scs[s.id] !== undefined && scs[s.id] !== null && String(scs[s.id]).trim() !== '') cnt++;
                          }
                        });
                        return presentCnt > 0 && cnt < presentCnt;
                      }
                      return true;
                    })
                    .map(agenda => {
                      const isSelected = selectedQuizAgendaId === agenda.id;
                      const existingGrade = assessmentGrades.find(g => g.id === agenda.id);
                      const scoresObj = existingGrade?.scores || recapValues[agenda.id] || {};
                      const classNorm = normalizeClass(agenda.class);
                      const classStudents = students.filter(s => normalizeClass(s.class) === classNorm && s.status === 'Aktif');
                      
                      let gradedCount = 0;
                      let presentStudentCount = 0;
                      let absentStudentCount = 0;
                      classStudents.forEach(s => {
                        const attRecord = attendanceRecords.find(r => r.studentId === s.id && r.agendaId === agenda.id);
                        const attStatusLower = attRecord?.status?.trim().toLowerCase();
                        const isAbsent = attStatusLower && (
                          attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
                          attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
                          attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
                          attStatusLower === AttendanceStatus.Izin.toLowerCase()
                        );
                        if (isAbsent) {
                          absentStudentCount++;
                        } else {
                          presentStudentCount++;
                          if (scoresObj[s.id] !== undefined && scoresObj[s.id] !== null && String(scoresObj[s.id]).trim() !== '') {
                            gradedCount++;
                          }
                        }
                      });
                      const isFullyGraded = presentStudentCount > 0 && gradedCount >= presentStudentCount;

                      return (
                        <div
                          key={agenda.id}
                          onClick={() => setSelectedQuizAgendaId(agenda.id)}
                          className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex flex-col justify-between min-h-[84px] gap-2 ${
                            isSelected
                              ? 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-500 shadow-sm shadow-cyan-500/10'
                              : 'bg-white/80 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 hover:border-cyan-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                              {new Date(agenda.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                            </span>
                            {isFullyGraded ? (
                              <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                <CheckCircle2 size={10} /> {gradedCount}/{presentStudentCount}
                              </span>
                            ) : (
                              <span className="text-[9px] font-bold text-amber-500 dark:text-amber-400 flex items-center gap-0.5">
                                <Clock size={10} /> {gradedCount}/{presentStudentCount}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] font-bold text-slate-800 dark:text-white leading-tight line-clamp-2">
                            {agenda.penilaian || 'Tugas Harian'}
                          </div>
                          {absentStudentCount > 0 && (
                            <div className="text-[8px] font-bold text-rose-500 dark:text-rose-400">
                              🚫 {absentStudentCount} Tidak Hadir
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Selected Agenda Info & Live Statistics Header */}
              {selectedQuizAgenda && (
                <div className="space-y-6">
                  <div className="p-5 bg-slate-900 dark:bg-slate-800 text-white rounded-3xl shadow-lg border border-slate-800 dark:border-slate-700 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2.5 py-0.5 bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 rounded-lg text-[10px] font-black uppercase tracking-wider">
                          Agenda Terpilih ({selectedQuizAgenda.date})
                        </span>
                        {selectedQuizAgenda.tpCode && (
                          <span className="px-2.5 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-lg text-[10px] font-black uppercase tracking-wider">
                            TP {selectedQuizAgenda.tpCode}
                          </span>
                        )}
                        {selectedQuizAgenda.penilaian && (
                          <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-lg text-[10px] font-black uppercase tracking-wider">
                            Asesmen: {selectedQuizAgenda.penilaian}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-black text-white">
                        {selectedQuizAgenda.topic || 'Topik KBM dan Asesmen Harian'}
                      </h3>
                      {selectedQuizAgenda.notes && (
                        <p className="text-xs text-cyan-200/90 font-medium pt-1">
                          <span className="font-bold text-cyan-300">Catatan Agenda:</span> {selectedQuizAgenda.notes}
                        </p>
                      )}
                    </div>

                    {/* Live Stats Pills */}
                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                      <div className="px-3 py-2 bg-slate-800/80 dark:bg-slate-700/80 rounded-2xl border border-slate-700 text-center min-w-[70px]">
                        <span className="block text-[9px] font-bold text-slate-400 uppercase">Siswa Hadir</span>
                        <span className="text-sm font-black text-white">{quizStats.presentTotal} <span className="text-[10px] text-slate-400 font-normal">/ {quizStats.total}</span></span>
                      </div>
                      <div className="px-3 py-2 bg-slate-800/80 dark:bg-slate-700/80 rounded-2xl border border-slate-700 text-center min-w-[80px]">
                        <span className="block text-[9px] font-bold text-cyan-400 uppercase">Terisi</span>
                        <span className="text-sm font-black text-cyan-300">{quizStats.filled} / {quizStats.presentTotal}</span>
                      </div>
                      {quizStats.absentCount > 0 && (
                        <div className="px-3 py-2 bg-rose-950/40 dark:bg-rose-900/40 rounded-2xl border border-rose-800/60 text-center min-w-[70px]">
                          <span className="block text-[9px] font-bold text-rose-400 uppercase">Tidak Hadir</span>
                          <span className="text-sm font-black text-rose-300">{quizStats.absentCount}</span>
                        </div>
                      )}
                      <div className="px-3 py-2 bg-slate-800/80 dark:bg-slate-700/80 rounded-2xl border border-slate-700 text-center min-w-[75px]">
                        <span className="block text-[9px] font-bold text-emerald-400 uppercase">Rata-Rata</span>
                        <span className="text-sm font-black text-emerald-300">{quizStats.avg}</span>
                      </div>
                      <div className="px-3 py-2 bg-slate-800/80 dark:bg-slate-700/80 rounded-2xl border border-slate-700 text-center min-w-[70px]">
                        <span className="block text-[9px] font-bold text-amber-400 uppercase">Max</span>
                        <span className="text-sm font-black text-amber-300">{quizStats.max}</span>
                      </div>
                      <div className="px-3 py-2 bg-slate-800/80 dark:bg-slate-700/80 rounded-2xl border border-slate-700 text-center min-w-[70px]">
                        <span className="block text-[9px] font-bold text-rose-400 uppercase">Min</span>
                        <span className="text-sm font-black text-rose-300">{quizStats.min}</span>
                      </div>
                    </div>
                  </div>

                  {/* Toolbar: Search, Batch Set, Paste */}
                  <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-3xl border border-slate-200 dark:border-slate-700/60">
                    <div className="relative flex-1">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input 
                        type="text" 
                        value={quizSearch}
                        onChange={e => setQuizSearch(e.target.value)}
                        placeholder="Cari nama siswa atau NIS..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold outline-none focus:ring-2 focus:ring-cyan-500/20 transition-all"
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-bold text-slate-400 mr-1">Isi Cepat:</span>
                      {[100, 95, 90, 85, 75].map(scorePreset => (
                        <button
                          key={scorePreset}
                          onClick={() => handleBatchSetQuizScore(scorePreset)}
                          className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-[11px] font-black hover:bg-cyan-50 dark:hover:bg-cyan-950/40 hover:text-cyan-600 hover:border-cyan-300 transition-all cursor-pointer"
                        >
                          {scorePreset}
                        </button>
                      ))}
                      <button
                        onClick={() => handleBatchSetQuizScore('')}
                        className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-rose-600 dark:text-rose-400 rounded-xl text-[11px] font-black hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-all cursor-pointer"
                      >
                        Reset All
                      </button>

                      <button
                        onClick={() => setShowPasteModal(true)}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-cyan-600 text-white rounded-xl text-[11px] font-black hover:bg-cyan-700 shadow-md shadow-cyan-900/10 transition-all cursor-pointer"
                      >
                        <Upload size={13} />
                        <span>Paste Masal</span>
                      </button>

                      <button
                        onClick={handleOpenSendToSumatifModal}
                        disabled={!selectedQuizAgenda || quizStats.filled === 0}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-[11px] font-black shadow-md shadow-indigo-900/20 active:scale-95 transition-all cursor-pointer border border-indigo-400/30"
                        title="Kirim nilai tugas harian ini ke Sumatif TP"
                      >
                        <Send size={13} />
                        <span>Kirim Nilai</span>
                      </button>
                    </div>
                  </div>

                  {/* PASTE MODAL */}
                  {showPasteModal && (
                    <div className="p-5 bg-cyan-50/80 dark:bg-cyan-950/40 rounded-3xl border-2 border-cyan-300 dark:border-cyan-800 space-y-3 animate-in fade-in duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Upload className="text-cyan-600 dark:text-cyan-400" size={18} />
                          <h4 className="text-xs font-black uppercase text-slate-800 dark:text-white tracking-wider">
                            Paste Masal Nilai Pemahaman (Excel / Text)
                          </h4>
                        </div>
                        <button 
                          onClick={() => setShowPasteModal(false)}
                          className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        Tempelkan daftar nilai per baris (misal dari Excel/WhatsApp). Nilai akan diisikan berurutan ke daftar siswa di bawah.
                      </p>
                      <textarea
                        rows={4}
                        value={pasteInputText}
                        onChange={e => setPasteInputText(e.target.value)}
                        placeholder={`85\n90\n78\n95\n...`}
                        className="w-full p-3 font-mono text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setShowPasteModal(false)}
                          className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl"
                        >
                          Batal
                        </button>
                        <button
                          onClick={handleApplyPasteScores}
                          className="px-5 py-2 bg-cyan-600 text-white text-xs font-black rounded-xl hover:bg-cyan-700 shadow-md"
                        >
                          Terapkan Nilai
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Student Table View */}
                  {filteredQuizStudents.length > 0 ? (
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                      {/* Desktop View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                          <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800">
                            <tr className="text-slate-400 uppercase text-[10px] font-black tracking-widest">
                              <th className="px-6 py-4 w-12 text-center">No</th>
                              <th className="px-6 py-4">Nama Siswa</th>
                              <th className="px-4 py-4 w-32 text-center">NISN / NIS</th>
                              <th className="px-6 py-4 w-52 text-center">Nilai Pemahaman (0 - 100)</th>
                              <th className="px-6 py-4 text-center">Status Capaian</th>
                              <th className="px-4 py-4 w-16 text-center">Aksi</th>
                            </tr>
                          </thead>
                           <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                            {filteredQuizStudents.map((student, idx) => {
                              const scoreVal = quizScores[student.id];
                              const hasValue = scoreVal !== '' && scoreVal !== null && scoreVal !== undefined;
                              const numVal = Number(scoreVal);

                              const attRecord = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === selectedQuizAgenda?.id);
                              const attStatusLower = attRecord?.status?.trim().toLowerCase();
                              const isAbsent = attStatusLower && (
                                attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
                                attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
                                attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
                                attStatusLower === AttendanceStatus.Izin.toLowerCase()
                              );

                              return (
                                <tr key={student.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                                  <td className="px-6 py-4 text-center font-mono text-xs text-slate-400 font-bold">
                                    {idx + 1}
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-black text-sm ${getStudentNameColor(student.gender)}`}>
                                        {student.name}
                                      </span>
                                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                                        {student.gender === 'Laki-laki' ? 'L' : 'P'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-center font-mono text-xs text-slate-500 dark:text-slate-400">
                                    {student.nisn || student.nis || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <div className="inline-flex items-center justify-center">
                                      <input 
                                        id={`quiz-score-input-${idx}`}
                                        type="number" 
                                        min="0"
                                        max="100"
                                        value={scoreVal ?? ''}
                                        onChange={e => handleQuizScoreChange(student.id, e.target.value, true)}
                                        onBlur={() => handleSaveQuizScores(undefined, true)}
                                        onFocus={e => e.target.select()}
                                        disabled={isAbsent}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            const next = document.getElementById(`quiz-score-input-${idx + 1}`);
                                            if (next) next.focus();
                                          } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            const prev = document.getElementById(`quiz-score-input-${idx - 1}`);
                                            if (prev) prev.focus();
                                          }
                                        }}
                                        placeholder={isAbsent ? "-" : "0"}
                                        className="w-24 px-3 py-2 text-center font-black font-mono text-base bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-900 disabled:cursor-not-allowed"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    {isAbsent ? (
                                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-3 py-1 rounded-full border uppercase tracking-wider ${
                                        attStatusLower === AttendanceStatus.Sakit.toLowerCase() ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' :
                                        attStatusLower === AttendanceStatus.Dispen.toLowerCase() ? 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20' :
                                        attStatusLower === AttendanceStatus.Izin.toLowerCase() ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20' :
                                        'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                                      }`}>
                                        🚫 Tidak Hadir ({attRecord?.status})
                                      </span>
                                    ) : hasValue ? (
                                      numVal >= 75 ? (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                          Tuntas ({numVal})
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                                          Perlu Pengayaan ({numVal})
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400">
                                        Belum Diisi
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4 text-center">
                                    {hasValue && !isAbsent && (
                                      <button
                                        onClick={() => handleQuizScoreChange(student.id, '')}
                                        className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors cursor-pointer"
                                        title="Hapus Nilai"
                                      >
                                        <X size={14} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile View */}
                      <div className="block md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                        {filteredQuizStudents.map((student, idx) => {
                          const scoreVal = quizScores[student.id];

                          const attRecord = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === selectedQuizAgenda?.id);
                          const attStatusLower = attRecord?.status?.trim().toLowerCase();
                          const isAbsent = attStatusLower && (
                            attStatusLower === AttendanceStatus.Sakit.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Dispen.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Alpa.toLowerCase() ||
                            attStatusLower === AttendanceStatus.Izin.toLowerCase()
                          );

                          return (
                            <div key={student.id} className="p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs text-slate-400 font-bold">{idx + 1}.</span>
                                  <span className={`font-black text-sm ${getStudentNameColor(student.gender)}`}>
                                    {student.name}
                                  </span>
                                </div>
                                {isAbsent && (
                                  <span className={`inline-flex items-center gap-1.5 text-[9px] font-black px-2.5 py-1 rounded-full border uppercase tracking-wider ${
                                    attStatusLower === AttendanceStatus.Sakit.toLowerCase() ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' :
                                    attStatusLower === AttendanceStatus.Dispen.toLowerCase() ? 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20' :
                                    attStatusLower === AttendanceStatus.Izin.toLowerCase() ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20' :
                                    'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                                  }`}>
                                    🚫 {attRecord?.status}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-2xl border border-slate-200/60 dark:border-slate-700/60">
                                <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Nilai Pemahaman:</span>
                                <input 
                                  id={`quiz-score-input-mob-${idx}`}
                                  type="number" 
                                  min="0"
                                  max="100"
                                  value={scoreVal ?? ''}
                                  onChange={e => handleQuizScoreChange(student.id, e.target.value, true)}
                                  onBlur={() => handleSaveQuizScores(undefined, true)}
                                  onFocus={e => e.target.select()}
                                  disabled={isAbsent}
                                  placeholder={isAbsent ? "-" : "0"}
                                  className="w-20 px-3 py-1.5 text-center font-black font-mono text-base bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-slate-100 outline-none disabled:opacity-50 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* BOTTOM ACTION BAR */}
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-5 bg-slate-900 text-white rounded-3xl border border-slate-800 shadow-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-black">
                            <ClipboardCheck size={20} />
                          </div>
                          <div>
                            <div className="text-xs font-black text-white">Status Penilaian Tugas Harian</div>
                            <div className="text-[11px] text-slate-400 font-medium">
                              <span className="text-cyan-300 font-bold">{quizStats.filled}</span> dari <span className="text-white font-bold">{quizStats.presentTotal}</span> siswa hadir terisi nilai (Rata-rata: <span className="text-emerald-400 font-bold">{quizStats.avg}</span>)
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-end">
                          <button
                            onClick={() => handleSaveQuizScores(undefined, false)}
                            disabled={isSavingQuiz}
                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-3 bg-slate-800 hover:bg-slate-700 active:scale-95 text-white font-black text-xs rounded-2xl border border-slate-700 transition-all cursor-pointer"
                          >
                            <Save size={15} />
                            <span>{isSavingQuiz ? 'Menyimpan...' : 'Simpan Nilai Tugas'}</span>
                          </button>

                          <button
                            onClick={handleOpenSendToSumatifModal}
                            disabled={!selectedQuizAgenda || quizStats.filled === 0}
                            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-indigo-950/40 border border-indigo-400/30 transition-all cursor-pointer"
                          >
                            <Send size={15} />
                            <span>Kirim Nilai ke Sumatif TP</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-16 text-center bg-slate-50/50 dark:bg-slate-800/20 rounded-[2.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                      <Search className="mx-auto mb-3 text-slate-300 dark:text-slate-600" size={40} />
                      <p className="text-slate-500 font-bold text-sm">Tidak ada siswa ditemukan di kelas {quizClass}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* EMPTY STATE WHEN NO AGENDA HAS "PEMBELAJARAN DAN ASESMEN" ACTIVITY TYPE */
            <div className="py-16 px-6 text-center bg-slate-50/60 dark:bg-slate-800/20 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800 space-y-4 max-w-2xl mx-auto">
              <div className="w-20 h-20 mx-auto bg-amber-50 dark:bg-amber-950/50 rounded-3xl flex items-center justify-center text-amber-500 border border-amber-200 dark:border-amber-900/40">
                <PenTool size={40} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-black text-slate-800 dark:text-slate-200">
                  Belum Ada Agenda dengan Jenis Kegiatan "KBM dan Asesmen Harian"
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                  Di kelas <span className="font-black text-indigo-600 dark:text-indigo-400">KELAS {quizClass}</span>, belum ada agenda mengajar yang dicatat dengan jenis kegiatan <span className="font-black text-amber-600 dark:text-amber-400">"KBM dan Asesmen Harian"</span>.
                </p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 text-left space-y-1.5 shadow-sm">
                <span className="font-black text-cyan-600 dark:text-cyan-400 block uppercase tracking-wider text-[10px]">
                  💡 Bantuan & Petunjuk:
                </span>
                <p>
                  1. Buka menu <strong>Agenda Mengajar</strong> pada dashboard guru.<br />
                  2. Tambah agenda baru atau edit agenda kelas {quizClass}.<br />
                  3. Pilih Jenis Kegiatan: <strong>"KBM dan Asesmen Harian"</strong>.<br />
                  4. Kembali ke halaman ini, maka form input penilaian tugas harian akan otomatis muncul secara interaktif.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : activeTab === 'formatif' ? (
        // VIEW FORMATIF TP
        <div className={`bg-gradient-to-br ${currentTheme.cardGradients[0] || 'from-indigo-600 via-indigo-700 to-blue-700'} p-5 sm:p-10 rounded-[2rem] sm:rounded-[2.5rem] shadow-xl text-white border border-white/20 pb-24 md:pb-10`}>
            <div className="mb-8 sm:mb-10 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 sm:gap-6">
                <div>
                    <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-none mb-1 flex items-center gap-3">
                        Formatif
                        {isSavingFormative && (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-widest animate-pulse">
                                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.6)]"></div>
                                Auto-Saving...
                            </span>
                        )}
                    </h2>
                    <p className="text-xs sm:text-sm text-white/80 font-medium">Penilaian kualitatif per Tujuan Pembelajaran secara mandiri.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => navigateClass(selectedClass, 'prev', setSelectedClass)}
                            title="Kelas Sebelumnya"
                            className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <div className="relative group flex-1 md:flex-none">
                            <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-slate-900 text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest z-10 uppercase">
                                Pilih Rombel
                            </label>
                            <select 
                                value={selectedClass} 
                                onChange={e => setSelectedClass(e.target.value)}
                                className="w-full md:w-48 pl-4 pr-10 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-slate-700 dark:text-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all text-xs appearance-none cursor-pointer"
                            >
                                <option value="">-- Pilih Kelas --</option>
                                {sortedClasses.map(c => <option key={c} value={c}>KELAS {c}</option>)}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-indigo-500 transition-colors">
                                <ChevronRight size={18} className="rotate-90" />
                            </div>
                        </div>
                        <button
                            onClick={() => navigateClass(selectedClass, 'next', setSelectedClass)}
                            title="Kelas Berikutnya"
                            className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => window.print()} 
                            disabled={!selectedClass}
                            className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
                            title="Cetak Data"
                        >
                            <Printer size={18} className="sm:hidden" />
                            <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Cetak</span>
                        </button>
                        <button 
                            onClick={handleExportFormatif}
                            disabled={!selectedClass}
                            className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-all cursor-pointer disabled:opacity-50"
                            title="Export Excel"
                        >
                            <FileSpreadsheet size={18} className="sm:hidden" />
                            <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Export</span>
                        </button>
                        <button 
                            onClick={() => handleSaveFormative(false)}
                            disabled={!selectedClass || !selectedTP || isSavingFormative}
                            className="flex items-center justify-center gap-3 px-6 py-3.5 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 shadow-xl shadow-indigo-900/10 active:scale-[0.98] disabled:opacity-50 transition-all text-sm grow md:grow-0 cursor-pointer"
                        >
                            {isSavingFormative ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span className="hidden sm:inline">MENYIMPAN...</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18} /> <span className="hidden sm:inline">SIMPAN CAPAIAN</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
                <div className="space-y-2">
                    <label className="text-[9px] sm:text-[10px] font-black text-indigo-100 uppercase tracking-widest ml-1">Pilih Tujuan Pembelajaran (TP)</label>
                    <select 
                        value={selectedTP} 
                        onChange={e => setSelectedTP(e.target.value)}
                        className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-indigo-600 dark:bg-indigo-950 border border-indigo-400/30 rounded-xl sm:rounded-2xl outline-none font-black text-white focus:ring-4 focus:ring-indigo-500/20 transition-all text-xs sm:text-sm"
                    >
                        <option value="">-- Pilih TP --</option>
                        {objectives.map(o => <option key={o.id} value={o.id}>{o.code}: {o.description.substring(0, 40)}...</option>)}
                    </select>
                </div>
            </div>

            {selectedClass && selectedTP ? (
                <>
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm text-left border-separate border-spacing-y-2">
                        <thead>
                            <tr className="text-slate-400 uppercase text-[10px] font-black tracking-[0.2em]">
                                <th className="px-6 py-4">No</th>
                                <th className="px-6 py-4">Siswa</th>
                                <th className="px-6 py-4 text-center">Formatif</th>
                                <th className="px-6 py-4 text-center">Nilai Sumatif</th>
                                {selectedTPData?.isPracticum && <th className="px-6 py-4 text-center">Nilai Praktikum</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStudentsForFormative.map((student, idx) => {
                                const currentLvl = formativeValues[student.id] || 'Layak';
                                return (
                                    <tr key={student.id} className="group transition-all">
                                        <td className="px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 rounded-l-2xl border-y border-l border-slate-100 dark:border-slate-800 font-black text-slate-400">{idx + 1}</td>
                                        <td className="px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 border-y border-slate-100 dark:border-slate-800">
                                            <p className={`font-black leading-none mb-1 ${getStudentNameColor(student.gender)}`}>{student.name}</p>
                                            <p className="text-[10px] font-bold text-slate-400">NISN: {student.nisn || student.nis || '-'}</p>
                                        </td>
                                        <td className="px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 border-y border-slate-100 dark:border-slate-800">
                                            <div className="flex flex-wrap items-center justify-center gap-2">
                                                {formativeLevels.map(lvl => {
                                                    const isActive = currentLvl === lvl;
                                                    return (
                                                        <button 
                                                            key={lvl}
                                                            onClick={() => handleLevelChange(student.id, lvl)}
                                                            className={`px-4 py-2 rounded-xl text-[10px] font-black transition-all border ${isActive 
                                                                ? `${getLevelColor(lvl)} scale-105 shadow-lg` 
                                                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400 hover:border-indigo-400'}`}
                                                        >
                                                            {lvl}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td className={`px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 border-y border-slate-100 dark:border-slate-800 ${!selectedTPData?.isPracticum ? 'rounded-r-2xl border-r' : ''}`}>
                                            <div className="flex flex-col items-center gap-2">
                                                <input 
                                                    type="number" step="any"
                                                    value={summativeValues[student.id] !== undefined ? summativeValues[student.id] : ''} 
                                                    onChange={(e) => handleSummativeChange(student.id, e.target.value)}
                                                    className="w-20 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-center font-black text-indigo-600 dark:text-indigo-400"
                                                />
                                                {formativeReferenceData[student.id]?.sumatifAvg > 0 && (
                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Ref Rekap: {formativeReferenceData[student.id].sumatifAvg}</span>
                                                )}
                                            </div>
                                        </td>
                                        {selectedTPData?.isPracticum && (
                                            <td className="px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 rounded-r-2xl border-y border-r border-slate-100 dark:border-slate-800 text-center">
                                                <div className="flex flex-col items-center gap-2">
                                                    <input 
                                                        type="number" 
                                                        min="0" 
                                                        max="100" 
                                                        value={praktikumValues[student.id] || ''} 
                                                        onChange={(e) => handlePraktikumChange(student.id, e.target.value)}
                                                        placeholder="0"
                                                        className="no-spinners w-20 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-center font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-sm"
                                                    />
                                                    {formativeReferenceData[student.id]?.praktikumAvg > 0 && (
                                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Ref Rekap: {formativeReferenceData[student.id].praktikumAvg}</span>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="md:hidden space-y-3">
                    {filteredStudentsForFormative.map((student, idx) => {
                        const currentLvl = formativeValues[student.id] || 'Layak';
                        return (
                            <div key={student.id} className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-100 dark:border-slate-800 space-y-3">
                                <div>
                                    <p className={`font-black text-xs sm:text-sm ${getStudentNameColor(student.gender)}`}>{idx + 1}. {student.name}</p>
                                    <p className="text-[9px] font-bold text-slate-400 mt-0.5">NISN: {student.nisn || student.nis || '-'}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {formativeLevels.map(lvl => {
                                        const isActive = currentLvl === lvl;
                                        return (
                                            <button 
                                                key={lvl}
                                                onClick={() => handleLevelChange(student.id, lvl)}
                                                className={`px-2 py-2 rounded-lg text-[9px] font-black transition-all border text-center ${isActive 
                                                    ? `${getLevelColor(lvl)} scale-105 shadow-sm` 
                                                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400'}`}
                                            >
                                                {lvl}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nilai Sumatif</label>
                                        <input 
                                            type="number" step="any"
                                            value={summativeValues[student.id] !== undefined ? summativeValues[student.id] : ''} 
                                            onChange={(e) => handleSummativeChange(student.id, e.target.value)}
                                            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-center font-black text-indigo-600 dark:text-indigo-400"
                                        />
                                        {formativeReferenceData[student.id]?.sumatifAvg > 0 && (
                                            <p className="text-[8px] font-black text-slate-400 uppercase text-center">Ref: {formativeReferenceData[student.id].sumatifAvg}</p>
                                        )}
                                    </div>
                                    {selectedTPData?.isPracticum && (
                                        <div className="space-y-1">
                                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nilai Praktikum</label>
                                            <input 
                                                type="number" step="any"
                                                min="0" 
                                                max="100" 
                                                value={praktikumValues[student.id] || ''} 
                                                onChange={(e) => handlePraktikumChange(student.id, e.target.value)}
                                                placeholder="0"
                                                className="no-spinners w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-center font-black text-indigo-600 dark:text-indigo-400 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-sm"
                                            />
                                            {formativeReferenceData[student.id]?.praktikumAvg > 0 && (
                                                <p className="text-[8px] font-black text-slate-400 uppercase text-center">Ref: {formativeReferenceData[student.id].praktikumAvg}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Sticky Mobile Save Button */}
                <div className="md:hidden fixed bottom-24 left-4 right-4 z-50">
                    <button 
                        onClick={() => handleSaveFormative(false)}
                        disabled={!selectedClass || !selectedTP || isSavingFormative}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-2xl shadow-indigo-900/40 active:scale-95 disabled:opacity-50 transition-all text-sm uppercase tracking-widest"
                    >
                        {isSavingFormative ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Menyimpan...
                            </>
                        ) : (
                            <>
                                <Save size={20} /> Simpan Capaian
                            </>
                        )}
                    </button>
                </div>
                </>
            ) : (
                <div className="py-20 text-center bg-slate-50/50 dark:bg-slate-800/10 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                    <Target className="mx-auto mb-4 text-slate-200" size={64} />
                    <p className="text-slate-400 font-bold italic">Silahkan pilih Kelas dan TP untuk melakukan penilaian formatif.</p>
                </div>
            )}
        </div>
      ) : activeTab === 'rekap-buku' ? (
        // VIEW REKAP BUKU CATATAN
        <div className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500 pb-24 md:pb-10 space-y-8">
          {/* Header Title & Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-950/60 rounded-3xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-inner border border-indigo-100 dark:border-indigo-900/40">
                <BookMarked size={32} />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-1">
                  Penilaian Rekap Buku Catatan
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 font-medium tracking-tight">
                  Pemeriksaan dan pencatatan jumlah tanda tangan (TTD) buku catatan siswa.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => navigateClass(notebookClass, 'prev', setNotebookClass)}
                  title="Kelas Sebelumnya"
                  className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="relative group flex-1 md:flex-none">
                  <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-slate-900 text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest z-10 uppercase">
                    Pilih Rombel
                  </label>
                  <select 
                    value={notebookClass} 
                    onChange={e => setNotebookClass(e.target.value)}
                    className="w-full md:w-48 pl-4 pr-10 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-slate-700 dark:text-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all text-sm appearance-none cursor-pointer"
                  >
                    <option value="">-- Pilih Kelas --</option>
                    {sortedClasses.map(c => <option key={c} value={c}>KELAS {c}</option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-indigo-500 transition-colors">
                    <ChevronRight size={18} className="rotate-90" />
                  </div>
                </div>
                <button
                  onClick={() => navigateClass(notebookClass, 'next', setNotebookClass)}
                  title="Kelas Berikutnya"
                  className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button 
                  onClick={() => window.print()} 
                  disabled={!notebookClass}
                  className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
                  title="Cetak Data"
                >
                  <Printer size={18} className="sm:hidden" />
                  <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Cetak</span>
                </button>
                <button 
                  onClick={handleExportNotebookExcel}
                  disabled={!notebookClass}
                  className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-all cursor-pointer disabled:opacity-50"
                  title="Export ke Excel"
                >
                  <FileSpreadsheet size={18} className="sm:hidden" />
                  <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Export</span>
                </button>
                <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800/60 text-indigo-700 dark:text-indigo-300 rounded-2xl text-xs font-bold shadow-xs">
                  {isSavingNotebook ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Menyimpan ke Firebase...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} className="text-emerald-500" />
                      <span>Tersimpan Otomatis ke Firebase</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {notebookClass && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 bg-gradient-to-br from-indigo-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-indigo-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Total Siswa</span>
                <div className="text-2xl font-black text-slate-800 dark:text-white font-mono">
                  {notebookStats.total} <span className="text-xs font-bold text-slate-400">Siswa</span>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-br from-emerald-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-emerald-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest block">Total TTD Terkumpul</span>
                <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono">
                  {notebookStats.totalTtd} <span className="text-xs font-bold text-slate-400">TTD</span>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-br from-amber-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-amber-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest block">Rata-rata TTD</span>
                <div className="text-2xl font-black text-amber-600 dark:text-amber-400 font-mono">
                  {notebookStats.avgTtd} <span className="text-xs font-bold text-slate-400">TTD / siswa</span>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-br from-rose-50 to-slate-50 dark:from-slate-800/60 dark:to-slate-800/20 rounded-2xl border border-rose-100 dark:border-slate-800 space-y-1">
                <span className="text-[10px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest block">TTD Tertinggi</span>
                <div className="text-2xl font-black text-rose-600 dark:text-rose-400 font-mono">
                  {notebookStats.maxTtd} <span className="text-xs font-bold text-slate-400">TTD</span>
                </div>
                <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 truncate mt-1" title={notebookStats.maxTtdStudents.join(', ')}>
                  {notebookStats.maxTtd > 0 ? notebookStats.maxTtdStudents.join(', ') : 'Belum ada'}
                </div>
              </div>
            </div>
          )}

          {/* Quick Toolbar: Search & Quick Reset */}
          {notebookClass && (
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-700/60">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input 
                  type="text" 
                  value={notebookSearch}
                  onChange={e => setNotebookSearch(e.target.value)}
                  placeholder="Cari nama siswa atau NIS..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all"
                />
              </div>

              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleBatchSetTtd(0)}
                  className="px-3.5 py-2 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-[11px] font-bold hover:bg-slate-300 dark:hover:bg-slate-600 transition-all cursor-pointer"
                >
                  Reset Semua TTD (0)
                </button>
              </div>
            </div>
          )}

          {/* Student Table View */}
          {notebookClass ? (
            filteredNotebookStudents.length > 0 ? (
              <div className="bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                {/* Desktop View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full text-sm text-left">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800">
                      <tr className="text-slate-400 uppercase text-[10px] font-black tracking-widest">
                        <th className="px-6 py-4 w-12 text-center">No</th>
                        <th className="px-6 py-4">Nama Siswa</th>
                        <th className="px-4 py-4 w-32 text-center">NISN / NIS</th>
                        <th className="px-6 py-4 w-52 text-center">Jumlah TTD</th>
                        <th className="px-6 py-4 w-40 text-center">Nilai Catatan</th>
                        <th className="px-6 py-4">Catatan / Keterangan Guru</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                      {filteredNotebookStudents.map((student, idx) => {
                        const entry = notebookEntries[student.id] || { jumlahTtd: 0, notes: '' };
                        const ttd = entry.jumlahTtd || 0;

                        return (
                          <tr key={student.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-6 py-4 text-center font-mono text-xs text-slate-400 font-bold">
                              {idx + 1}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className={`font-black text-sm ${getStudentNameColor(student.gender)}`}>
                                  {student.name}
                                </span>
                                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                                  {student.gender === 'Laki-laki' ? 'L' : 'P'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center font-mono text-xs text-slate-500 dark:text-slate-400">
                              {student.nisn || student.nis || '-'}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="inline-flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                                <button 
                                  onClick={() => handleTtdChange(student.id, -1)}
                                  className="w-8 h-8 rounded-xl bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-black text-base flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all shadow-sm active:scale-90 cursor-pointer"
                                  title="Kurangi TTD"
                                >
                                  -
                                </button>
                                <input 
                                  type="number" 
                                  min="0"
                                  max="99"
                                  value={ttd}
                                  onChange={e => handleTtdSetDirect(student.id, e.target.value)}
                                  className="w-12 text-center font-black font-mono text-base bg-transparent text-slate-800 dark:text-slate-100 outline-none"
                                />
                                <button 
                                  onClick={() => handleTtdChange(student.id, 1)}
                                  className="w-8 h-8 rounded-xl bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-black text-base flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all shadow-sm active:scale-90 cursor-pointer"
                                  title="Tambah TTD"
                                >
                                  +
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-block px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 font-mono font-black text-sm">
                                {notebookStats.maxTtd > 0 ? Math.round((ttd / notebookStats.maxTtd) * 100) : 0}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1.5">
                                <input 
                                  type="text" 
                                  value={entry.notes || ''}
                                  onChange={e => handleNotebookNotesChange(student.id, e.target.value, false)}
                                  onBlur={() => handleSaveNotebook(true)}
                                  placeholder="Tambah keterangan..."
                                  className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 transition-all"
                                />
                                <div className="flex flex-wrap gap-1">
                                  {['Rapi & Lengkap', 'Kurang Lengkap', 'Belum Diperiksa'].map(preset => (
                                    <button 
                                      key={preset}
                                      onClick={() => handleNotebookNotesChange(student.id, preset, true)}
                                      className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950 transition-colors cursor-pointer"
                                    >
                                      + {preset}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View */}
                <div className="block md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredNotebookStudents.map((student, idx) => {
                    const entry = notebookEntries[student.id] || { jumlahTtd: 0, notes: '' };
                    const ttd = entry.jumlahTtd || 0;

                    return (
                      <div key={student.id} className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-slate-400 font-bold">{idx + 1}.</span>
                            <span className={`font-black text-sm ${getStudentNameColor(student.gender)}`}>
                              {student.name}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-2xl border border-slate-200/60 dark:border-slate-700/60">
                          <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Jumlah TTD:</span>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => handleTtdChange(student.id, -1)}
                              className="w-8 h-8 rounded-xl bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-black text-base flex items-center justify-center border border-slate-200 dark:border-slate-600"
                            >
                              -
                            </button>
                            <span className="font-mono font-black text-lg text-slate-800 dark:text-slate-100 min-w-8 text-center">
                              {ttd}
                            </span>
                            <button 
                              onClick={() => handleTtdChange(student.id, 1)}
                              className="w-8 h-8 rounded-xl bg-indigo-600 text-white font-black text-base flex items-center justify-center shadow-md shadow-indigo-500/20"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between bg-indigo-50/40 dark:bg-indigo-950/20 p-2.5 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30">
                          <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Nilai Catatan:</span>
                          <span className="font-mono font-black text-base text-indigo-600 dark:text-indigo-400">
                            {notebookStats.maxTtd > 0 ? Math.round((ttd / notebookStats.maxTtd) * 100) : 0}
                          </span>
                        </div>

                        <input 
                          type="text" 
                          value={entry.notes || ''}
                          onChange={e => handleNotebookNotesChange(student.id, e.target.value, false)}
                          onBlur={() => handleSaveNotebook(true)}
                          placeholder="Catatan guru..."
                          className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 outline-none"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-16 text-center bg-slate-50/50 dark:bg-slate-800/20 rounded-[2.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                <Search className="mx-auto mb-3 text-slate-300 dark:text-slate-600" size={40} />
                <p className="text-slate-500 font-bold text-sm">Tidak ada siswa yang ditemukan di kelas {notebookClass}</p>
              </div>
            )
          ) : (
            <div className="py-20 text-center bg-slate-50/50 dark:bg-slate-800/10 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <BookMarked className="mx-auto mb-4 text-slate-300 dark:text-slate-600" size={56} />
              <p className="text-slate-400 font-bold">Silakan pilih kelas untuk menginput rekap TTD buku catatan.</p>
            </div>
          )}
        </div>
      ) : (
        // VIEW REKAP NILAI
        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 pb-24 md:pb-10">
            <div className="mb-8 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-600 dark:text-slate-400 hidden sm:block">
                        <BarChart3 size={32} />
                    </div>
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-1">Penilaian Sumatif dan Praktikum TP</h2>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Matriks nilai per siswa untuk kelas yang dipilih.</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => navigateClass(rekapClass, 'prev', setRekapClass)}
                            title="Kelas Sebelumnya"
                            className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <div className="relative group flex-1 md:flex-none">
                            <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-slate-900 text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest z-10 uppercase">
                                Pilih Rombel
                            </label>
                            <select 
                                value={rekapClass} 
                                onChange={e => setRekapClass(e.target.value)}
                                className="w-full md:w-32 pl-4 pr-10 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-slate-700 dark:text-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all text-xs appearance-none cursor-pointer"
                            >
                                <option value="">-- Kelas --</option>
                                {sortedClasses.map(c => <option key={c} value={c}>KELAS {c}</option>)}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-indigo-500 transition-colors">
                                <ChevronRight size={18} className="rotate-90" />
                            </div>
                        </div>
                        <button
                            onClick={() => navigateClass(rekapClass, 'next', setRekapClass)}
                            title="Kelas Berikutnya"
                            className="p-3.5 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 rounded-2xl border-2 border-slate-100 dark:border-slate-700 transition-all cursor-pointer"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="relative group flex-1 md:flex-none">
                        <label className="absolute -top-2 left-3 px-1 bg-white dark:bg-slate-900 text-[10px] font-black text-slate-500 tracking-widest z-10 uppercase">
                            Jenis
                        </label>
                        <select 
                            value={rekapAssessmentType} 
                            onChange={e => setRekapAssessmentType(e.target.value)}
                            className="w-full md:w-48 pl-4 pr-10 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-slate-700 dark:text-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/5 transition-all text-xs appearance-none cursor-pointer"
                        >
                            <option value="Semua">Semua Jenis</option>
                            <option value="Sumatif per TP">Sumatif TP</option>
                            <option value="Praktikum TP">Praktikum TP</option>
                            <option value="SAS">Sumatif Akhir</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 transition-colors">
                            <ChevronRight size={18} className="rotate-90" />
                        </div>
                    </div>

                    <button 
                        onClick={() => window.print()} 
                        disabled={!rekapClass}
                        className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
                        title="Cetak Data"
                    >
                        <Printer size={18} className="sm:hidden" />
                        <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Cetak</span>
                    </button>
                    <button 
                        onClick={handleExportRecap}
                        disabled={!rekapClass || recapData.rows.length === 0}
                        className="flex items-center justify-center gap-2 p-3.5 sm:px-5 sm:py-3.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-all cursor-pointer disabled:opacity-50"
                        title="Export Excel"
                    >
                        <FileSpreadsheet size={18} className="sm:hidden" />
                        <span className="hidden sm:inline font-black text-xs uppercase tracking-widest">Export</span>
                    </button>
                    <button 
                        onClick={() => setShowSaveConfirmModal(true)}
                        disabled={!rekapClass || Object.keys(recapValues).length === 0}
                        className={`flex items-center justify-center gap-3 px-6 py-3.5 bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white font-black rounded-2xl shadow-xl shadow-black/10 active:scale-[0.98] disabled:opacity-50 transition-all text-sm grow md:grow-0 cursor-pointer`}
                    >
                        <Save size={18} /> <span className="hidden sm:inline">SIMPAN REKAP</span>
                    </button>
                </div>
            </div>
            
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4 p-4 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bobot:</span>
                        <div className="flex items-center gap-2">
                            <label className="text-[10px] font-bold text-slate-500">Sumatif:</label>
                            <input 
                                type="number" step="any"
                                value={sumatifWeight} 
                                onChange={e => {
                                    const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                    setSumatifWeight(val);
                                    setPraktikumWeight(100 - val);
                                }}
                                className="w-12 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl text-center font-black text-xs text-indigo-600 focus:border-indigo-400 outline-none"
                            />
                            <span className="text-[10px] font-bold text-slate-400">%</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-[10px] font-bold text-slate-500">Praktikum:</label>
                            <input 
                                type="number" step="any"
                                value={praktikumWeight} 
                                onChange={e => {
                                    const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                    setPraktikumWeight(val);
                                    setSumatifWeight(100 - val);
                                }}
                                className="w-12 px-2 py-1.5 bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl text-center font-black text-xs text-emerald-600 focus:border-emerald-400 outline-none"
                            />
                            <span className="text-[10px] font-bold text-slate-400">%</span>
                        </div>
                    </div>
                    <button 
                        onClick={() => handleSaveWeights()}
                        disabled={isSavingWeights || (sumatifWeight === activeUser.sumatifWeight && praktikumWeight === activeUser.praktikumWeight)}
                        className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white font-black rounded-xl hover:opacity-90 disabled:opacity-50 transition-all text-[10px] uppercase tracking-widest shadow-md`}
                    >
                        {isSavingWeights ? (
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <Save size={12} />
                        )}
                        Simpan Bobot
                    </button>
                </div>
                <div className="hidden lg:block text-[10px] text-slate-400 italic font-medium">
                    * Klik header kolom (<b>Nama Siswa, Jumlah, Rata2</b>) untuk mengurutkan data.
                </div>
            </div>

            <div className="hidden md:block overflow-x-auto custom-scrollbar pb-4">
                <table className="min-w-full text-xs text-center border-collapse border border-slate-200 dark:border-slate-800">
                    <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 text-sm uppercase tracking-wider font-black">
                            <th className="sticky left-0 bg-slate-50 dark:bg-slate-800 z-20 p-0 border border-slate-200 dark:border-slate-800 text-left min-w-[180px] text-indigo-500">
                                <div className="flex items-stretch h-full min-h-[44px]">
                                    <div className="w-10 flex items-center justify-center border-r border-slate-200 dark:border-slate-800 shrink-0">
                                        No
                                    </div>
                                    <button 
                                        onClick={() => handleRekapSort('studentName')}
                                        className="flex-1 flex items-center justify-between px-3 py-3 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                                    >
                                        <span>Nama Siswa</span>
                                        <ArrowUpDown size={14} className={`opacity-0 group-hover:opacity-100 transition-opacity ${rekapSortConfig.key === 'studentName' ? 'opacity-100 text-indigo-500' : 'text-slate-400'}`} />
                                    </button>
                                </div>
                            </th>
                            {recapData.columns.map((col, i) => {
                                const isSumatif = col.label === ActivityType.SUMATIF || col.label === 'Sumatif per TP';
                                const isPraktikum = col.label === ActivityType.PRAKTIKUM || col.label === 'Praktikum TP';
                                const colorClass = isSumatif ? 'text-indigo-600 dark:text-indigo-400' : isPraktikum ? 'text-emerald-600 dark:text-emerald-400' : 'text-cyan-600 dark:text-cyan-400';
                                const badgeBg = isSumatif ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800' : isPraktikum ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700';
                                
                                return (
                                <th key={col.id} className={`px-2 py-3 border border-slate-200 dark:border-slate-800 min-w-[85px] ${colorClass}`}>
                                    <div className="flex flex-col items-center">
                                        <span className="text-xs font-black mb-0.5">{col.tpCode}</span>
                                        <span className={`text-[9px] font-extrabold leading-tight uppercase px-1.5 py-0.5 rounded-md border ${badgeBg}`}>
                                            {isSumatif ? 'Sumatif' : isPraktikum ? 'Praktikum' : col.label}
                                        </span>
                                        {col.label !== ActivityType.SUMATIF && col.label !== ActivityType.PRAKTIKUM && (
                                            <span className="text-[9px] mt-1 opacity-70">{col.date.split('-').reverse().slice(0, 2).join('/')}</span>
                                        )}
                                        {col.isDeletable && (
                                            <button 
                                                onClick={() => {
                                                    setAssessmentToDelete(col.id);
                                                    setShowConfirmModal(true);
                                                }}
                                                className="mt-1 p-1 text-red-500 hover:text-red-700 transition-colors"
                                                title="Hapus Kolom Penilaian"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                </th>
                            )})}
                            <th className="sticky right-[80px] bg-slate-50 dark:bg-slate-800 z-20 p-0 border border-slate-200 dark:border-slate-800 text-slate-500 min-w-[80px]">
                                <button 
                                    onClick={() => handleRekapSort('total')}
                                    className="w-full h-full flex flex-col items-center justify-center py-3 px-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                                >
                                    <span className="flex items-center gap-1">
                                        Jumlah
                                        <ArrowUpDown size={12} className={`transition-opacity ${rekapSortConfig.key === 'total' ? 'opacity-100 text-indigo-500' : 'opacity-0 group-hover:opacity-100 text-slate-400'}`} />
                                    </span>
                                </button>
                            </th>
                            <th className="sticky right-0 bg-indigo-50 dark:bg-indigo-900/30 z-20 p-0 border border-slate-200 dark:border-slate-800 text-indigo-600 dark:text-indigo-400 min-w-[80px]">
                                <button 
                                    onClick={() => handleRekapSort('average')}
                                    className="w-full h-full flex flex-col items-center justify-center py-3 px-2 hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-colors group"
                                >
                                    <span className="flex items-center gap-1">
                                        Rata2
                                        <ArrowUpDown size={12} className={`transition-opacity ${rekapSortConfig.key === 'average' ? 'opacity-100 text-indigo-500' : 'opacity-0 group-hover:opacity-100 text-slate-400'}`} />
                                    </span>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {recapData.rows.map((row, idx) => (
                            <tr id={`grad-student-${row.studentId}`} key={row.studentId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                <td className={`sticky left-0 bg-white dark:bg-slate-900 z-10 p-0 border border-slate-200 dark:border-slate-800 font-bold text-left whitespace-nowrap ${getStudentNameColor(row.gender)}`}>
                                    <div className="flex items-stretch h-full min-h-[36px]">
                                        <div className="w-10 flex items-center justify-center border-r border-slate-200 dark:border-slate-800 shrink-0 text-[10px] text-slate-400 font-medium">
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1 flex flex-col justify-center px-3 py-2">
                                            <button 
                                                onClick={() => {
                                                  const student = students.find(s => s.id === row.studentId);
                                                  if (student && onStudentRecapClick) onStudentRecapClick(student);
                                                }}
                                                className="text-[11px] hover:text-indigo-600 hover:underline transition-colors text-left font-bold"
                                            >
                                                {row.studentName}
                                            </button>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[9px] font-black text-emerald-500 uppercase tracking-tighter">Hadir: {studentAttendanceStats[row.studentId]?.percentage}%</span>
                                            {(studentAttendanceStats[row.studentId]?.alpa || 0) > 0 && <span className="text-xs font-black text-rose-600 dark:text-rose-400 uppercase bg-rose-50 dark:bg-rose-900/20 px-1.5 py-0.5 rounded-lg border border-rose-200 dark:border-rose-800 underline decoration-2 underline-offset-2">ALPA: {studentAttendanceStats[row.studentId]?.alpa}</span>}
                                            {(studentAttendanceStats[row.studentId]?.sakit || 0) > 0 && <span className="text-[9px] font-black text-amber-500 uppercase">S: {studentAttendanceStats[row.studentId]?.sakit}</span>}
                                            {(studentAttendanceStats[row.studentId]?.izin || 0) > 0 && <span className="text-[9px] font-black text-indigo-500 uppercase">I: {studentAttendanceStats[row.studentId]?.izin}</span>}
                                            {(studentAttendanceStats[row.studentId]?.dispen || 0) > 0 && <span className="text-[9px] font-black text-cyan-500 uppercase">D: {studentAttendanceStats[row.studentId]?.dispen}</span>}
                                        </div>
                                        </div>
                                    </div>
                                </td>
                                {recapData.columns.map(col => (
                                    <td key={`${row.studentId}-${col.id}`} className="px-1 py-2 border border-slate-200 dark:border-slate-800">
                                        <input 
                                            type="number" step="any"
                                            value={row.scores[col.id] != null ? row.scores[col.id] : ''}
                                            onChange={(e) => handleRecapScoreChange(col.id, row.studentId, e.target.value)}
                                            className="no-spinners w-full max-w-[50px] px-1 py-1 bg-transparent border-none text-center font-bold text-xs text-slate-700 dark:text-slate-200 outline-none"
                                            placeholder="-"
                                            min="0"
                                            max="100"
                                        />
                                    </td>
                                ))}
                                <td className="sticky right-[80px] bg-white dark:bg-slate-900 z-10 px-3 py-2 border border-slate-200 dark:border-slate-800 font-bold text-sm text-slate-500">
                                    {row.total}
                                </td>
                                <td className="sticky right-0 bg-indigo-50/50 dark:bg-indigo-900/20 z-10 px-3 py-2 border border-slate-200 dark:border-slate-800 font-bold text-sm text-indigo-600 dark:text-indigo-400">
                                    {row.average}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MOBILE VIEW: CARDS */}
            <div className="md:hidden">
                <div className="space-y-3">
                    {recapData.rows.map((row, idx) => (
                    <div id={`grad-student-${row.studentId}`} key={row.studentId} className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-400">{idx + 1}.</span>
                                <div className="flex flex-col">
                                    <button 
                                        onClick={() => {
                                          const student = students.find(s => s.id === row.studentId);
                                          if (student && onStudentRecapClick) onStudentRecapClick(student);
                                        }}
                                        className={`font-bold text-sm hover:text-indigo-600 hover:underline transition-colors text-left ${getStudentNameColor(row.gender)}`}
                                    >
                                        {row.studentName}
                                    </button>
                                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                        <span className="text-[9px] font-black text-emerald-500 uppercase tracking-tight">Hadir: {studentAttendanceStats[row.studentId]?.percentage}%</span>
                                        {(studentAttendanceStats[row.studentId]?.alpa || 0) > 0 && <span className="text-xs font-black text-rose-600 dark:text-rose-400 uppercase bg-rose-50 dark:bg-rose-900/20 px-2 py-1 rounded-xl border-2 border-rose-200 dark:border-rose-800 shadow-sm animate-pulse">ALPA: {studentAttendanceStats[row.studentId]?.alpa}</span>}
                                        {(studentAttendanceStats[row.studentId]?.sakit || 0) > 0 && <span className="text-[9px] font-black text-amber-500 uppercase">S: {studentAttendanceStats[row.studentId]?.sakit}</span>}
                                        {(studentAttendanceStats[row.studentId]?.izin || 0) > 0 && <span className="text-[9px] font-black text-indigo-500 uppercase">I: {studentAttendanceStats[row.studentId]?.izin}</span>}
                                        {(studentAttendanceStats[row.studentId]?.dispen || 0) > 0 && <span className="text-[9px] font-black text-cyan-500 uppercase">D: {studentAttendanceStats[row.studentId]?.dispen}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <div className="bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded-lg text-[9px] font-bold text-slate-500">
                                    T: {row.total}
                                </div>
                                <div className="bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded-lg text-[9px] font-bold text-indigo-600 dark:text-indigo-400">
                                    R: {row.average}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {recapData.columns.map(col => (
                                <div key={col.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 p-2 rounded-lg border border-slate-100 dark:border-slate-700">
                                    <div className="flex flex-col">
                                        <span className="text-[8px] font-bold text-indigo-500">{col.tpCode}</span>
                                        <span className="text-[7px] text-slate-400">{col.label}</span>
                                    </div>
                                    <input 
                                        type="number" step="any"
                                        value={row.scores[col.id] != null ? row.scores[col.id] : ''}
                                        onChange={(e) => handleRecapScoreChange(col.id, row.studentId, e.target.value)}
                                        className="no-spinners w-10 bg-transparent text-right font-bold text-sm text-slate-700 dark:text-slate-200 outline-none"
                                        placeholder="-"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            </div>

            {/* Sticky Mobile Save Button */}
            <div className="md:hidden fixed bottom-24 left-4 right-4 z-50">
                <button 
                    onClick={() => setShowSaveConfirmModal(true)}
                    disabled={!rekapClass || Object.keys(recapValues).length === 0}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 text-white font-black rounded-xl shadow-lg active:scale-95 disabled:opacity-50 transition-colors text-sm uppercase tracking-wider"
                >
                    <Save size={20} /> Simpan Rekap
                </button>
            </div>
        </div>
      )}
      
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-2xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-black text-slate-800 dark:text-white mb-2">Konfirmasi Hapus</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Apakah Anda yakin ingin menghapus penilaian ini? Tindakan ini tidak dapat dibatalkan.</p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 transition-colors"
              >
                Batal
              </button>
              <button 
                onClick={() => assessmentToDelete && handleDeleteAssessment(assessmentToDelete)}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl shadow-2xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-black text-slate-800 dark:text-white mb-2">Konfirmasi Simpan</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Apakah Anda yakin ingin menyimpan perubahan rekapitulasi nilai ini?</p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowSaveConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 transition-colors"
              >
                Batal
              </button>
              <button 
                onClick={() => handleSaveRecap()}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors"
              >
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl max-w-sm w-full mx-4 flex flex-col items-center text-center border border-slate-100 dark:border-slate-800 animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-3xl flex items-center justify-center mb-6 shadow-inner rotate-3">
              <CheckCircle2 size={40} />
            </div>
            <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">Berhasil Disimpan!</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">{saveSuccessMessage}</p>
          </div>
        </div>
      )}

      {/* SEND QUIZ SCORES TO SUMATIF TP MODAL */}
      {showSendToSumatifModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[90vh] rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 sm:p-7 bg-gradient-to-r from-indigo-600 via-indigo-700 to-indigo-800 text-white flex items-center justify-between shrink-0 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24 blur-3xl pointer-events-none"></div>
              <div className="flex items-center gap-3 relative z-10">
                <div className="w-12 h-12 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center text-white shadow-inner">
                  <Send size={22} />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-black tracking-tight">Kirim Nilai ke Sumatif TP</h3>
                  <p className="text-indigo-100 text-xs font-medium mt-0.5">
                    Transfer nilai tugas harian ke buku penilaian Sumatif per TP
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSendToSumatifModal(false)}
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors relative z-10"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 sm:p-7 overflow-y-auto space-y-6 flex-1 text-slate-800 dark:text-slate-100">
              {/* Source Info Card */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 space-y-2">
                <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-slate-400">
                  <span>Sumber Nilai (Tugas Harian)</span>
                  <span className="text-cyan-600 dark:text-cyan-400 font-bold">Kelas {quizClass}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="font-black text-sm text-slate-800 dark:text-white">
                    {selectedQuizAgenda?.topic || 'Agenda KBM dan Asesmen Harian'}
                  </div>
                  <div className="text-xs text-slate-500 font-bold">
                    Tanggal: <span className="text-slate-700 dark:text-slate-300 font-mono">{selectedQuizAgenda?.date}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-200/60 dark:border-slate-700/60">
                  <span className="px-2.5 py-1 bg-cyan-100 dark:bg-cyan-950 text-cyan-700 dark:text-cyan-300 rounded-lg text-xs font-bold">
                    {quizStats.filled} Siswa Terisi Nilai
                  </span>
                  <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs font-bold">
                    Rata-rata: {quizStats.avg}
                  </span>
                  {selectedQuizAgenda?.penilaian && (
                    <span className="px-2.5 py-1 bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-bold">
                      {selectedQuizAgenda.penilaian}
                    </span>
                  )}
                </div>
              </div>

              {/* Step 1: Select TP */}
              <div className="space-y-2">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  1. Pilih Tujuan Pembelajaran (TP) Target:
                </label>
                <select
                  value={targetSumatifTpId}
                  onChange={(e) => setTargetSumatifTpId(e.target.value)}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border-2 border-indigo-200 dark:border-indigo-800/80 rounded-2xl text-xs sm:text-sm font-bold text-slate-800 dark:text-white outline-none focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all cursor-pointer"
                >
                  <option value="">-- Silakan Pilih Tujuan Pembelajaran (TP) --</option>
                  {(objectives || []).filter(o => o.isSummative || o.isPracticum).map((obj) => (
                    <option key={obj.id} value={obj.id}>
                      {obj.code}: {obj.description.length > 60 ? obj.description.substring(0, 60) + '...' : obj.description} {obj.isSummative ? '(Sumatif)' : '(Praktikum)'}
                    </option>
                  ))}
                </select>

                {/* Selected TP Info Card */}
                {(() => {
                  const currentSelectedObj = objectives.find(o => o.id === targetSumatifTpId);
                  if (!currentSelectedObj) return null;
                  return (
                    <div className="p-4 bg-indigo-50/70 dark:bg-indigo-950/40 rounded-2xl border border-indigo-200 dark:border-indigo-800/60 space-y-1.5 animate-in fade-in duration-150">
                      <div className="flex items-center justify-between">
                        <span className="px-2.5 py-0.5 bg-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-wider">
                          {currentSelectedObj.code}
                        </span>
                        {currentSelectedObj.scope && (
                          <span className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300">
                            Materi: {currentSelectedObj.scope}
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 leading-relaxed">
                        {currentSelectedObj.description}
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Step 2: Target Category (Sumatif / Praktikum) */}
              <div className="space-y-2">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  2. Kategori Kolom Penilaian:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTargetSumatifActivityType(ActivityType.SUMATIF)}
                    className={`p-3.5 rounded-2xl border-2 text-left flex items-center gap-3 transition-all cursor-pointer ${
                      targetSumatifActivityType === ActivityType.SUMATIF
                        ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/50 text-indigo-900 dark:text-white shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${
                      targetSumatifActivityType === ActivityType.SUMATIF ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                    }`}>
                      <Target size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-black">Sumatif per TP</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Nilai Sumatif Utama (Rekap TP)</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTargetSumatifActivityType(ActivityType.PRAKTIKUM)}
                    className={`p-3.5 rounded-2xl border-2 text-left flex items-center gap-3 transition-all cursor-pointer ${
                      targetSumatifActivityType === ActivityType.PRAKTIKUM
                        ? 'border-amber-600 bg-amber-50/70 dark:bg-amber-950/50 text-amber-900 dark:text-white shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${
                      targetSumatifActivityType === ActivityType.PRAKTIKUM ? 'bg-amber-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                    }`}>
                      <PenTool size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-black">Praktikum TP</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Nilai Kinerja / Praktik</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Step 3: Student Preview Table */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    3. Pratinjau Nilai Siswa yang Akan Ditransfer:
                  </label>
                  <span className="text-[11px] font-bold text-slate-400">
                    Total: {filteredQuizStudents.filter(s => {
                      const v = quizScores[s.id];
                      return v !== '' && v !== null && v !== undefined;
                    }).length} Siswa
                  </span>
                </div>
                
                <div className="max-h-48 overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-2 space-y-1">
                  {filteredQuizStudents.map((s, i) => {
                    const score = quizScores[s.id];
                    const att = attendanceRecords.find(r => r.studentId === s.id && r.agendaId === selectedQuizAgenda?.id);
                    const attLower = att?.status?.trim().toLowerCase();
                    const isAbs = attLower && (
                      attLower === AttendanceStatus.Sakit.toLowerCase() ||
                      attLower === AttendanceStatus.Dispen.toLowerCase() ||
                      attLower === AttendanceStatus.Alpa.toLowerCase() ||
                      attLower === AttendanceStatus.Izin.toLowerCase()
                    );
                    const hasScore = score !== '' && score !== null && score !== undefined && !isAbs;

                    return (
                      <div key={s.id} className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] font-bold text-slate-400 w-5">{i + 1}.</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200 truncate">{s.name}</span>
                          {isAbs && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-600 font-bold">
                              Tidak Hadir ({att?.status})
                            </span>
                          )}
                        </div>
                        <div className="shrink-0 font-mono font-black">
                          {hasScore ? (
                            <span className={`px-2 py-0.5 rounded-lg text-xs ${Number(score) >= 75 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'}`}>
                              {score}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 sm:p-6 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setShowSendToSumatifModal(false)}
                disabled={isSendingToSumatif}
                className="px-5 py-2.5 rounded-2xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleExecuteSendToSumatif}
                disabled={!targetSumatifTpId || isSendingToSumatif}
                className="flex items-center gap-2 px-6 py-2.5 rounded-2xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-indigo-900/20 active:scale-95 transition-all cursor-pointer"
              >
                {isSendingToSumatif ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    <span>Mengirim Nilai...</span>
                  </>
                ) : (
                  <>
                    <Send size={15} />
                    <span>Kirim Nilai Sekarang</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      
      {/* STUDENT SUMMARY MODAL */}
      {selectedStudentForSummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4 backdrop-blur-sm animate-in fade-in zoom-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800">
            {/* Modal Header */}
            <div className="p-6 sm:p-8 bg-indigo-600 text-white flex items-center justify-between shrink-0 relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
               <div className="relative z-10">
                 <h3 className="text-xl sm:text-2xl font-black tracking-tight">{selectedStudentForSummary.name}</h3>
                 <p className="text-indigo-100 text-xs font-bold uppercase tracking-widest mt-1">NISN: {selectedStudentForSummary.nisn || selectedStudentForSummary.nis || '-'} • KELAS {selectedStudentForSummary.class}</p>
               </div>
               <button 
                onClick={() => setSelectedStudentForSummary(null)}
                className="relative z-10 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
               >
                 <X size={24} />
               </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-8 scrollbar-hide">
              {/* Statistik Kehadiran */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-xl">
                    <Clock size={20} />
                  </div>
                  <h4 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">Kehadiran (Mata Pelajaran Anda)</h4>
                </div>
                
                <div className="flex gap-4">
                   <div className="flex-1 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-2xl flex items-center justify-between px-6">
                      <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Hadir</p>
                      <p className="text-xl font-black text-emerald-600 dark:text-emerald-400">{studentSummaryData?.attendance.hadir}</p>
                   </div>
                   <div className="flex-1 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-2xl flex items-center justify-between px-6">
                      <p className="text-[10px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest">Alpa</p>
                      <p className="text-xl font-black text-rose-600 dark:text-rose-400">{studentSummaryData?.attendance.alpa}</p>
                   </div>
                </div>
                <p className="text-center text-[9px] font-bold text-slate-400 mt-2 uppercase tracking-widest opacity-60">Total {studentSummaryData?.attendance.total} Pertemuan</p>
              </section>

              <hr className="border-slate-100 dark:border-slate-800" />

              {/* Nilai Sumatif */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 rounded-xl">
                    <Award size={20} />
                  </div>
                  <h4 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">Nilai Sumatif</h4>
                </div>
                
                {studentSummaryData?.summative && studentSummaryData.summative.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {studentSummaryData.summative.map((grade, idx) => {
                      const tp = objectives.find(o => o.id === grade.tpId);
                      return (
                        <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                          <div className="flex-1 min-w-0 pr-3">
                            <p className="text-[10px] font-black text-slate-400 uppercase mb-0.5">{tp?.code || 'Sumatif'}</p>
                            <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{tp?.description || 'N/A'}</p>
                          </div>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shadow-sm ${grade.score >= 75 ? 'bg-emerald-100 text-emerald-600 border border-emerald-200' : 'bg-rose-100 text-rose-600 border border-rose-200'}`}>
                            {grade.score}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-800">
                    <Award className="mx-auto text-slate-300 mb-3" size={32} />
                    <p className="text-sm font-bold text-slate-400 italic">Belum ada data nilai sumatif</p>
                  </div>
                )}
              </section>

              {/* Nilai Praktikum */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-xl">
                    <PenTool size={20} />
                  </div>
                  <h4 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">Nilai Praktikum</h4>
                </div>
                
                {studentSummaryData?.praktikum && studentSummaryData.praktikum.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {studentSummaryData.praktikum.map((grade, idx) => {
                      const tp = objectives.find(o => o.id === grade.tpId);
                      return (
                        <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                          <div className="flex-1 min-w-0 pr-3">
                            <p className="text-[10px] font-black text-slate-400 uppercase mb-0.5">{tp?.code || 'Praktikum'}</p>
                            <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{tp?.description || 'N/A'}</p>
                          </div>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shadow-sm ${grade.score >= 75 ? 'bg-amber-100 text-amber-600 border border-amber-200' : 'bg-rose-100 text-rose-600 border border-rose-200'}`}>
                            {grade.score}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-800">
                    <PenTool className="mx-auto text-slate-300 mb-3" size={32} />
                    <p className="text-sm font-bold text-slate-400 italic">Belum ada data nilai praktikum</p>
                  </div>
                )}
              </section>
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-center shrink-0">
               <button 
                 onClick={() => setSelectedStudentForSummary(null)}
                 className="px-10 py-3 bg-slate-900 dark:bg-white dark:text-slate-900 text-white rounded-2xl text-sm font-black uppercase tracking-widest shadow-xl"
               >
                 Tutup
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GradingView;
